import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import mapboxgl, { Map } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Activity,
  BarChart3,
  Cat,
  ChevronDown,
  Database,
  Download,
  Droplet,
  Eraser,
  EyeOff,
  Layers,
  MapPin,
  MousePointer2,
  Navigation,
  Palette,
  PenLine,
  Pentagon,
  Play,
  Radio,
  RectangleHorizontal,
  RefreshCw,
  Bell,
  BellRing,
  Pencil,
  Plus,
  Settings,
  ShieldAlert,
  Smartphone,
  Square,
  Trash2,
  Type as TypeIcon,
  Wifi,
  X
} from "lucide-react";
import "./styles.css";

type Config = {
  appName: string;
  home: { lat: number; lon: number; label: string };
  mapboxToken: string;
  beaconIds: string[];
  vapidPublicKey?: string | null;
  mqtt: { tcpHost: string | null; tcpPort: number; wsPort: number; topic: string; embedded: boolean; internalOnly: boolean };
};

type AlertDirection = "inside" | "outside";
type AlertNotifyMode = "always" | "once_on_request";

type AlertArea = {
  id: string;
  name: string;
  polygon: Array<[number, number]>;
  direction: AlertDirection;
  notifyMode: AlertNotifyMode;
  buttonText: string | null;
  color: string;
  enabled: boolean;
  armed: boolean;
  armedForDevice?: boolean;
  armedCount?: number;
  lastInside: boolean | null;
  lastNotifiedAt: string | null;
  currentlyInside: boolean | null;
  conditionMet: boolean | null;
};

type Receiver = {
  id: string;
  room: string;
  name: string;
  lat: number | null;
  lon: number | null;
  heightM: number | null;
  placement: string | null;
  isActive: boolean;
};

type Estimate = {
  beaconId: string;
  lat: number;
  lon: number;
  accuracyM: number | null;
  source: string;
  isLive: boolean;
  estimatedAt: string;
  modelId?: string | null;
  details: Record<string, unknown>;
};

type Signal = {
  receiverId: string | null;
  room: string;
  beaconId: string;
  rssi: number | null;
  distanceM: number | null;
  observedAt: string;
};

type ReceiverTelemetry = {
  id: string;
  room: string;
  receiverId: string | null;
  observedAt: string;
  ageSeconds: number;
  ip: string | null;
  uptimeS: number | null;
  firmware: string | null;
  version: string | null;
  wifiRssi: number | null;
};

type ModelInfo = {
  id: string;
  createdAt: string;
  engine: string;
  featureCount: number;
  sampleCount: number;
  metrics: Record<string, unknown>;
} | null;

type AppState = {
  estimate: Estimate;
  receivers: Receiver[];
  signals: Signal[];
  receiverTelemetry: ReceiverTelemetry[];
  model: ModelInfo;
  training: { activeSessionId: string | null; sampleCount: number };
  mqtt: { enabled: boolean; embedded_broker: boolean; subscriber_connected: boolean; error: string | null } | null;
  alertAreas?: AlertArea[];
  serverTime?: string;
};

type WalkTelemetry = ReceiverTelemetry | null;

type WalkBleSignal = {
  observedAt: string;
  ageSeconds: number;
  beaconId: string;
  rssi: number | null;
  distanceM: number | null;
  intervalMs: number | null;
} | null;

type WalkTestState = {
  board: {
    label: string;
    room: string;
    usbPort: string;
    hardware: string;
    firmware: string;
    mac: string;
    mqttRoomTopic: string;
    role: string;
  };
  beacon: { id: string; name: string; expectedLocation: string };
  wifi: WalkTelemetry;
  ble: WalkBleSignal;
  history: {
    wifi: Array<{ observedAt: string; wifiRssi: number | null }>;
    ble: Array<{ observedAt: string; rssi: number | null; distanceM: number | null }>;
  };
  mqtt: { enabled: boolean; embedded_broker: boolean; subscriber_connected: boolean; error: string | null } | null;
};

type UserLocation = {
  lat: number;
  lon: number;
  accuracy: number | null;
  heading: number | null;
};

type TrainingWalkSession = {
  id: string;
  beaconId: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  sampleCount: number;
  modelId: string | null;
  notes: string | null;
};

type TrainingWalkSample = {
  id: string;
  capturedAt: string;
  lat: number;
  lon: number;
  gpsAccuracyM: number | null;
  labelSource: string;
  zoneLabel: string | null;
  notes: string | null;
};

type TrainingWalk = {
  session: TrainingWalkSession | null;
  samples: TrainingWalkSample[];
};

type TrainingSummary = {
  beaconId: string;
  totalSamples: number;
  finishedSessions: number;
  hasActiveModel: boolean;
  activeModel: ModelInfo | null;
};

type AvailabilityBoard = {
  boardKey: string;
  label: string;
  room: string;
  uptimePct: number;
  sampleCount: number;
  lastSeenAt: string | null;
  buckets: number[];
};

type AvailabilityResponse = {
  range: "24h" | "7d";
  windowStart: string;
  windowEnd: string;
  bucketSeconds: number;
  bucketCount: number;
  gapThresholdSeconds: number;
  boards: AvailabilityBoard[];
};

type MovementRange = "10m" | "60m" | "6h" | "24h";
type MovementMode = "trail" | "heatmap" | null;

type PositionHistoryPoint = {
  id: string;
  beaconId: string;
  lat: number;
  lon: number;
  accuracyM: number | null;
  source: string;
  isLive: boolean;
  estimatedAt: string;
  modelId?: string | null;
  details: Record<string, unknown>;
};

type PositionHistoryResponse = {
  range: MovementRange;
  windowStart: string;
  windowEnd: string;
  points: PositionHistoryPoint[];
  limited: boolean;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

type DrawingMode = "select" | "line" | "rectangle" | "polygon" | "text" | "eyedropper" | "erase";
type DrawingShapeType = "line" | "rectangle" | "polygon" | "text";

type DrawingFeature = {
  id: string;
  type: DrawingShapeType;
  coordinates: Array<[number, number]>;
  color: string;
  text?: string;
  createdAt: string;
};

type SettingsSectionKey =
  | "movement"
  | "alerts"
  | "annotations"
  | "notifications"
  | "install"
  | "mqtt"
  | "model"
  | "training"
  | "receivers"
  | "diagnostics";

const apiBase = import.meta.env.VITE_API_BASE_URL || "";
const buildToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || "";
const CAT_SOURCE_ID = "siegi-cat-source";
const CAT_ICON_LAYER_ID = "siegi-cat-icon";
const CAT_IMAGE_ID = "siegi-head";
const CAT_IMAGE_URL = "/siegi-map.png";
const RECEIVER_SOURCE_ID = "siegi-receiver-source";
const RECEIVER_CIRCLE_LAYER_ID = "siegi-receiver-circle";
const RECEIVER_LABEL_LAYER_ID = "siegi-receiver-label";
const TRAINING_WALK_SOURCE_ID = "siegi-training-walk-source";
const TRAINING_WALK_LINE_LAYER_ID = "siegi-training-walk-line";
const TRAINING_WALK_SAMPLE_LAYER_ID = "siegi-training-walk-samples";
const TRAINING_WALK_ENDPOINT_LAYER_ID = "siegi-training-walk-endpoints";
const TRAINING_WALK_ENDPOINT_LABEL_LAYER_ID = "siegi-training-walk-endpoint-labels";
const MOVEMENT_SOURCE_ID = "siegi-movement-source";
const MOVEMENT_HEATMAP_SOURCE_ID = "siegi-movement-heatmap-source";
const MOVEMENT_ARROW_SOURCE_ID = "siegi-movement-arrow-source";
const MOVEMENT_LINE_LAYER_ID = "siegi-movement-line";
const MOVEMENT_POINT_LAYER_ID = "siegi-movement-points";
const MOVEMENT_ENDPOINT_LAYER_ID = "siegi-movement-endpoints";
const MOVEMENT_ENDPOINT_LABEL_LAYER_ID = "siegi-movement-endpoint-labels";
const MOVEMENT_ARROW_LAYER_ID = "siegi-movement-arrows";
const MOVEMENT_HEATMAP_LAYER_ID = "siegi-movement-heatmap";
const DRAWING_LINE_SOURCE_ID = "siegi-drawing-line-source";
const DRAWING_FILL_SOURCE_ID = "siegi-drawing-fill-source";
const DRAWING_TEXT_SOURCE_ID = "siegi-drawing-text-source";
const DRAWING_PREVIEW_SOURCE_ID = "siegi-drawing-preview-source";
const DRAWING_LINE_LAYER_ID = "siegi-drawing-lines";
const DRAWING_FILL_LAYER_ID = "siegi-drawing-fills";
const DRAWING_TEXT_LAYER_ID = "siegi-drawing-text";
const DRAWING_PREVIEW_LINE_LAYER_ID = "siegi-drawing-preview-line";
const DRAWING_PREVIEW_FILL_LAYER_ID = "siegi-drawing-preview-fill";
const ALERT_AREA_SOURCE_ID = "siegi-alert-area-source";
const ALERT_AREA_FILL_LAYER_ID = "siegi-alert-area-fill";
const ALERT_AREA_LINE_LAYER_ID = "siegi-alert-area-line";
const ALERT_AREA_LABEL_LAYER_ID = "siegi-alert-area-label";
const RECEIVER_ONLINE_SECONDS = 180;
const STATE_POLL_MS = 3000;
const SETTINGS_POLL_MS = 15000;
const STALE_DATA_SECONDS = 12;
const TRAINING_SAMPLE_INTERVAL_MS = 3000;
const TRAINING_SAMPLE_INTERVAL_SECONDS = TRAINING_SAMPLE_INTERVAL_MS / 1000;
const INSTALL_DISMISS_COUNT_KEY = "siegi.installPrompt.dismissCount";
const INSTALL_NEXT_PROMPT_AT_KEY = "siegi.installPrompt.nextPromptAt";
const DRAWING_STORAGE_KEY = "siegi.mapAnnotations.v1";
const DEFAULT_DRAWING_COLOR = "#e7583f";

function formatDistance(meters: number | null | undefined) {
  if (meters == null || Number.isNaN(meters)) return "-";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDbm(value: number | null | undefined) {
  return value == null || Number.isNaN(value) ? "-" : `${Math.round(value)} dBm`;
}

function formatAgo(iso: string) {
  const ageMs = Date.now() - new Date(iso).getTime();
  if (ageMs < 0) return "0 s";
  const sec = Math.round(ageMs / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 48) return `${hours} Std.`;
  return `${Math.round(hours / 24)} Tagen`;
}

function signalQuality(value: number | null | undefined, kind: "wifi" | "ble") {
  if (value == null || Number.isNaN(value)) return { label: "waiting", className: "missing" };
  const strong = kind === "wifi" ? -60 : -70;
  const usable = kind === "wifi" ? -72 : -86;
  const weak = kind === "wifi" ? -82 : -96;
  if (value >= strong) return { label: "strong", className: "strong" };
  if (value >= usable) return { label: "ok", className: "ok" };
  if (value >= weak) return { label: "weak", className: "weak" };
  return { label: "marginal", className: "bad" };
}

function findReceiverTelemetry(appState: AppState | null, receiver: Receiver) {
  return (appState?.receiverTelemetry || []).find((item) => item.receiverId === receiver.id || item.room === receiver.room) || null;
}

function isReceiverOnline(telemetry: ReceiverTelemetry | null) {
  return Boolean(telemetry && telemetry.ageSeconds <= RECEIVER_ONLINE_SECONDS);
}

function formatReceiverWifi(telemetry: ReceiverTelemetry | null) {
  if (!telemetry) return "-";
  const ageLabel = telemetry.ageSeconds > RECEIVER_ONLINE_SECONDS ? ` · vor ${formatAgo(telemetry.observedAt)}` : "";
  return `${formatDbm(telemetry.wifiRssi)}${ageLabel}`;
}

function formatMqttTcp(config: Config | null) {
  if (!config) return "-";
  if (config.mqtt.tcpHost) return `${config.mqtt.tcpHost}:${config.mqtt.tcpPort}`;
  return config.mqtt.internalOnly ? `internal (${config.mqtt.tcpPort})` : `Port ${config.mqtt.tcpPort}`;
}

function haversineMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const r = 6371000;
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

function bearingDegrees(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const toDeg = (value: number) => (value * 180) / Math.PI;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function movementRangeLabel(range: MovementRange) {
  const labels: Record<MovementRange, string> = {
    "10m": "10 min",
    "60m": "60 min",
    "6h": "6 h",
    "24h": "24 h"
  };
  return labels[range];
}

function isStandaloneApp() {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function isMobileInstallSurface() {
  return window.matchMedia("(max-width: 760px), (pointer: coarse)").matches;
}

function isIosSafari() {
  const ua = navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  // Exclude in-app browsers / Chrome-on-iOS where add-to-home-screen is unavailable.
  const isSafari = /^((?!crios|fxios|edgios|opios).)*safari/i.test(ua);
  return isIos && isSafari;
}

function installPromptSnoozed() {
  const nextAt = Number(window.localStorage.getItem(INSTALL_NEXT_PROMPT_AT_KEY) || "0");
  return Number.isFinite(nextAt) && Date.now() < nextAt;
}

function snoozeInstallPrompt() {
  const dismissCount = Number(window.localStorage.getItem(INSTALL_DISMISS_COUNT_KEY) || "0") + 1;
  const days = dismissCount === 1 ? 1 : dismissCount === 2 ? 3 : 5;
  window.localStorage.setItem(INSTALL_DISMISS_COUNT_KEY, String(dismissCount));
  window.localStorage.setItem(INSTALL_NEXT_PROMPT_AT_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
}

function registerFreshServiceWorker(onUpdateReady: () => void) {
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .then((registration) => {
      registration.update().catch(() => undefined);
      const activateWaitingWorker = () => {
        if (!registration.waiting) return;
        onUpdateReady();
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      };
      activateWaitingWorker();
      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            onUpdateReady();
            worker.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
      const updateOnFocus = () => {
        if (document.visibilityState === "visible") {
          registration.update().catch(() => undefined);
        }
      };
      document.addEventListener("visibilitychange", updateOnFocus);
    })
    .catch(() => undefined);
}

function signalPercent(value: number | null | undefined, kind: "wifi" | "ble") {
  if (value == null || Number.isNaN(value)) return 0;
  const min = kind === "wifi" ? -92 : -105;
  const max = kind === "wifi" ? -42 : -55;
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
}

function makeDrawingId() {
  return `drawing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function loadLegacyLocalDrawings(): DrawingFeature[] {
  try {
    const raw = window.localStorage.getItem(DRAWING_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.id === "string" && typeof item.type === "string");
  } catch {
    return [];
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function rectangleCoordinates(a: [number, number], b: [number, number]): Array<[number, number]> {
  return [
    a,
    [b[0], a[1]],
    b,
    [a[0], b[1]],
    a
  ];
}

function ensureClosedPolygon(points: Array<[number, number]>) {
  if (points.length === 0) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return points;
  return [...points, first];
}

function distanceToSegmentMeters(
  point: { lat: number; lon: number },
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
) {
  const ab = haversineMeters(a, b);
  if (ab === 0) return haversineMeters(point, a);
  const ap = haversineMeters(a, point);
  const bp = haversineMeters(b, point);
  const s = (ab + ap + bp) / 2;
  const area = Math.max(0, Math.sqrt(Math.max(0, s * (s - ab) * (s - ap) * (s - bp))));
  return (2 * area) / ab;
}

function simplifyCoordinates(points: Array<[number, number]>, toleranceM = 1.4): Array<[number, number]> {
  if (points.length <= 2) return points;
  let maxDistance = 0;
  let maxIndex = 0;
  const first = points[0];
  const last = points[points.length - 1];
  for (let i = 1; i < points.length - 1; i += 1) {
    const distance = distanceToSegmentMeters(
      { lon: points[i][0], lat: points[i][1] },
      { lon: first[0], lat: first[1] },
      { lon: last[0], lat: last[1] }
    );
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  if (maxDistance <= toleranceM) return [first, last];
  const left = simplifyCoordinates(points.slice(0, maxIndex + 1), toleranceM);
  const right = simplifyCoordinates(points.slice(maxIndex), toleranceM);
  return [...left.slice(0, -1), ...right];
}

function compactMovementPoints(points: PositionHistoryPoint[]) {
  const compacted: PositionHistoryPoint[] = [];
  const minimumDistanceM = points.length > 1000 ? 3.5 : points.length > 300 ? 2.0 : 0.8;
  for (const point of points) {
    const previous = compacted[compacted.length - 1];
    if (!previous || haversineMeters(previous, point) >= minimumDistanceM) {
      compacted.push(point);
    } else {
      compacted[compacted.length - 1] = point;
    }
  }
  if (points.length > 0 && compacted[compacted.length - 1]?.id !== points[points.length - 1].id) {
    compacted.push(points[points.length - 1]);
  }
  const coordinates = compacted.map((point) => [point.lon, point.lat] as [number, number]);
  const toleranceM = points.length > 1000 ? 5.0 : points.length > 300 ? 3.2 : 1.2;
  const simplifiedCoordinates = simplifyCoordinates(coordinates, toleranceM);
  const coordinateKeys = new Set(simplifiedCoordinates.map((coord) => `${coord[0].toFixed(7)},${coord[1].toFixed(7)}`));
  const simplified = compacted.filter((point, index) => {
    if (index === 0 || index === compacted.length - 1) return true;
    return coordinateKeys.has(`${point.lon.toFixed(7)},${point.lat.toFixed(7)}`);
  });
  return simplified.length >= 2 ? simplified : compacted;
}

const emptyFeatureCollection = {
  type: "FeatureCollection",
  features: []
};

type ReceiverDragOverrides = Record<string, { lat: number; lon: number }>;

function receiverFeatureCollection(
  appState: AppState | null,
  receiverEditMode: boolean,
  overrides: ReceiverDragOverrides = {}
) {
  if (!appState || !receiverEditMode) return emptyFeatureCollection;
  return {
    type: "FeatureCollection",
    features: appState.receivers
      .filter((receiver) => receiver.isActive && receiver.lat != null && receiver.lon != null)
      .map((receiver) => {
        const override = overrides[receiver.id];
        const signal = appState.signals.find((item) => item.receiverId === receiver.id || item.room === receiver.room);
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [override?.lon ?? receiver.lon, override?.lat ?? receiver.lat]
          },
          properties: {
            id: receiver.id,
            label: receiverShortLabel(receiver),
            hasSignal: !!signal,
            title: `${receiver.name}${signal?.rssi != null ? ` / ${Math.round(signal.rssi)} dBm` : ""}`
          }
        };
      })
  };
}

function catFeatureCollection(estimate: Estimate | null | undefined) {
  if (!estimate) return emptyFeatureCollection;
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: [estimate.lon, estimate.lat] },
        properties: {
          live: estimate.isLive
        }
      }
    ]
  };
}

function ensureCatImage(map: Map) {
  if (map.hasImage(CAT_IMAGE_ID)) return;
  map.loadImage(CAT_IMAGE_URL, (error, image) => {
    if (error || !image || map.hasImage(CAT_IMAGE_ID)) return;
    map.addImage(CAT_IMAGE_ID, image);
  });
}

function trainingWalkFeatureCollection(walk: TrainingWalk | null, visible: boolean) {
  if (!visible || !walk || walk.samples.length === 0) return emptyFeatureCollection;
  const coordinates = walk.samples.map((sample) => [sample.lon, sample.lat]);
  const features: object[] = [];
  if (coordinates.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates },
      properties: { kind: "line" }
    });
  }
  walk.samples.forEach((sample, index) => {
    const isStart = index === 0;
    const isEnd = index === walk.samples.length - 1;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [sample.lon, sample.lat] },
      properties: {
        kind: isStart ? "start" : isEnd ? "end" : "sample",
        label: isStart ? "S" : isEnd ? "E" : "",
        accuracy: sample.gpsAccuracyM ?? null
      }
    });
  });
  return { type: "FeatureCollection", features };
}

function movementFeatureCollection(history: PositionHistoryResponse | null, mode: MovementMode) {
  if (!history || mode !== "trail" || history.points.length === 0) return emptyFeatureCollection;
  const points = compactMovementPoints(history.points);
  const coordinates = points.map((point) => [point.lon, point.lat]);
  const features: object[] = [];
  if (coordinates.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates },
      properties: { kind: "line" }
    });
  }
  const sampleStep = Math.max(1, Math.ceil(points.length / 80));
  points.forEach((point, index) => {
    const isStart = index === 0;
    const isEnd = index === points.length - 1;
    if (!isStart && !isEnd && index % sampleStep !== 0) return;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point.lon, point.lat] },
      properties: {
        kind: isStart ? "start" : isEnd ? "end" : "sample",
        label: isStart ? "Start" : isEnd ? "Now" : "",
        age: Date.now() - new Date(point.estimatedAt).getTime()
      }
    });
  });
  return { type: "FeatureCollection", features };
}

function movementArrowFeatureCollection(history: PositionHistoryResponse | null, mode: MovementMode) {
  if (!history || mode !== "trail" || history.points.length < 2) return emptyFeatureCollection;
  const points = compactMovementPoints(history.points);
  const step = Math.max(1, Math.floor(points.length / 18));
  const features: object[] = [];
  for (let index = step; index < points.length; index += step) {
    const previous = points[index - 1];
    const point = points[index];
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point.lon, point.lat] },
      properties: {
        bearing: bearingDegrees(previous, point)
      }
    });
  }
  return { type: "FeatureCollection", features };
}

function movementHeatmapFeatureCollection(history: PositionHistoryResponse | null, mode: MovementMode) {
  if (!history || mode !== "heatmap" || history.points.length === 0) return emptyFeatureCollection;
  const now = Date.now();
  const points = history.points;
  const windowMs = Math.max(1, new Date(history.windowEnd).getTime() - new Date(history.windowStart).getTime());
  return {
    type: "FeatureCollection",
    features: points.map((point) => {
      const recency = 1 - Math.max(0, Math.min(1, (now - new Date(point.estimatedAt).getTime()) / windowMs));
      const accuracy = point.accuracyM == null ? 1 : Math.max(0.25, Math.min(1, 1 - point.accuracyM / 160));
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [point.lon, point.lat] },
        properties: {
          weight: 0.35 + recency * 0.4 + accuracy * 0.25
        }
      };
    })
  };
}

function drawingFeatureCollections(drawings: DrawingFeature[]) {
  const lineFeatures: object[] = [];
  const fillFeatures: object[] = [];
  const textFeatures: object[] = [];
  drawings.forEach((drawing) => {
    const baseProperties = { id: drawing.id, color: drawing.color, text: drawing.text || "" };
    if (drawing.type === "line") {
      lineFeatures.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: drawing.coordinates },
        properties: baseProperties
      });
      return;
    }
    if (drawing.type === "text") {
      const point = drawing.coordinates[0];
      if (!point) return;
      textFeatures.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: point },
        properties: baseProperties
      });
      return;
    }
    const polygon = ensureClosedPolygon(drawing.coordinates);
    fillFeatures.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [polygon] },
      properties: baseProperties
    });
    lineFeatures.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: polygon },
      properties: baseProperties
    });
  });
  return {
    lines: { type: "FeatureCollection", features: lineFeatures },
    fills: { type: "FeatureCollection", features: fillFeatures },
    texts: { type: "FeatureCollection", features: textFeatures }
  };
}

function drawingPreviewFeatureCollection(mode: DrawingMode, points: Array<[number, number]>, hover: [number, number] | null, color: string) {
  if (mode === "line" && points.length === 1 && hover) {
    return {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: { type: "LineString", coordinates: [points[0], hover] },
        properties: { color }
      }]
    };
  }
  if (mode === "rectangle" && points.length === 1 && hover) {
    return {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [rectangleCoordinates(points[0], hover)] },
        properties: { color }
      }]
    };
  }
  if (mode === "polygon" && points.length > 0) {
    const coordinates = hover ? [...points, hover] : points;
    return {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: { type: "LineString", coordinates },
        properties: { color }
      }]
    };
  }
  return emptyFeatureCollection;
}

function getGeoJsonSource(map: Map, id: string) {
  return map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
}

function setGeoJsonSourceData(map: Map, id: string, data: object) {
  getGeoJsonSource(map, id)?.setData(data as never);
}

function ensurePositionLayers(map: Map) {
  if (!map.getSource(CAT_SOURCE_ID)) {
    map.addSource(CAT_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getSource(RECEIVER_SOURCE_ID)) {
    map.addSource(RECEIVER_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getLayer(RECEIVER_CIRCLE_LAYER_ID)) {
    map.addLayer({
      id: RECEIVER_CIRCLE_LAYER_ID,
      type: "circle",
      source: RECEIVER_SOURCE_ID,
      paint: {
        "circle-radius": 17,
        "circle-color": ["case", ["boolean", ["get", "hasSignal"], false], "#2f6f5d", "#285a8f"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-opacity": 0.95
      }
    });
  }
  if (!map.getLayer(RECEIVER_LABEL_LAYER_ID)) {
    map.addLayer({
      id: RECEIVER_LABEL_LAYER_ID,
      type: "symbol",
      source: RECEIVER_SOURCE_ID,
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 12,
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#ffffff"
      }
    });
  }
  ensureCatImage(map);
  if (!map.getLayer(CAT_ICON_LAYER_ID)) {
    map.addLayer({
      id: CAT_ICON_LAYER_ID,
      type: "symbol",
      source: CAT_SOURCE_ID,
      layout: {
        "icon-image": CAT_IMAGE_ID,
        "icon-size": [
          "interpolate", ["linear"], ["zoom"],
          14, 0.25,
          18, 0.5,
          21, 0.9
        ],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center"
      },
      paint: {
        "icon-opacity": ["case", ["boolean", ["get", "live"], false], 1.0, 0.55]
      }
    });
  }
}

function ensureTrainingWalkLayers(map: Map) {
  if (!map.getSource(TRAINING_WALK_SOURCE_ID)) {
    map.addSource(TRAINING_WALK_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  const beforeCat = map.getLayer(CAT_ICON_LAYER_ID) ? CAT_ICON_LAYER_ID : undefined;
  if (!map.getLayer(TRAINING_WALK_LINE_LAYER_ID)) {
    map.addLayer({
      id: TRAINING_WALK_LINE_LAYER_ID,
      type: "line",
      source: TRAINING_WALK_SOURCE_ID,
      filter: ["==", ["get", "kind"], "line"],
      layout: {
        "line-cap": "round",
        "line-join": "round"
      },
      paint: {
        "line-color": "#e7583f",
        "line-opacity": 0.9,
        "line-width": ["interpolate", ["linear"], ["zoom"], 17, 4, 20, 10]
      }
    }, beforeCat);
  }
  if (!map.getLayer(TRAINING_WALK_SAMPLE_LAYER_ID)) {
    map.addLayer({
      id: TRAINING_WALK_SAMPLE_LAYER_ID,
      type: "circle",
      source: TRAINING_WALK_SOURCE_ID,
      filter: ["==", ["get", "kind"], "sample"],
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 17, 3, 20, 6],
        "circle-color": "#ffc457",
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        "circle-opacity": 0.95
      }
    }, beforeCat);
  }
  if (!map.getLayer(TRAINING_WALK_ENDPOINT_LAYER_ID)) {
    map.addLayer({
      id: TRAINING_WALK_ENDPOINT_LAYER_ID,
      type: "circle",
      source: TRAINING_WALK_SOURCE_ID,
      filter: ["in", ["get", "kind"], ["literal", ["start", "end"]]],
      paint: {
        "circle-radius": 14,
        "circle-color": ["match", ["get", "kind"], "start", "#269f5c", "end", "#2c5cbe", "#e7583f"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 3,
        "circle-opacity": 0.98
      }
    }, beforeCat);
  }
  if (!map.getLayer(TRAINING_WALK_ENDPOINT_LABEL_LAYER_ID)) {
    map.addLayer({
      id: TRAINING_WALK_ENDPOINT_LABEL_LAYER_ID,
      type: "symbol",
      source: TRAINING_WALK_SOURCE_ID,
      filter: ["in", ["get", "kind"], ["literal", ["start", "end"]]],
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 12,
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#ffffff"
      }
    }, beforeCat);
  }
}

function ensureMovementLayers(map: Map) {
  if (!map.getSource(MOVEMENT_SOURCE_ID)) {
    map.addSource(MOVEMENT_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never, lineMetrics: true });
  }
  if (!map.getSource(MOVEMENT_HEATMAP_SOURCE_ID)) {
    map.addSource(MOVEMENT_HEATMAP_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getSource(MOVEMENT_ARROW_SOURCE_ID)) {
    map.addSource(MOVEMENT_ARROW_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  const beforeCat = map.getLayer(CAT_ICON_LAYER_ID) ? CAT_ICON_LAYER_ID : undefined;
  if (!map.getLayer(MOVEMENT_HEATMAP_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_HEATMAP_LAYER_ID,
      type: "heatmap",
      source: MOVEMENT_HEATMAP_SOURCE_ID,
      paint: {
        "heatmap-weight": ["get", "weight"],
        "heatmap-intensity": 1,
        "heatmap-radius": 34,
        "heatmap-opacity": 0.68,
        "heatmap-color": [
          "interpolate", ["linear"], ["heatmap-density"],
          0, "rgba(47,111,93,0)",
          0.22, "rgba(47,111,93,0.45)",
          0.48, "rgba(255,196,87,0.68)",
          0.76, "rgba(231,88,63,0.78)",
          1, "rgba(145,58,50,0.86)"
        ]
      }
    }, beforeCat);
  }
  if (!map.getLayer(MOVEMENT_LINE_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_LINE_LAYER_ID,
      type: "line",
      source: MOVEMENT_SOURCE_ID,
      filter: ["==", ["get", "kind"], "line"],
      layout: {
        "line-cap": "round",
        "line-join": "round"
      },
      paint: {
        "line-width": ["interpolate", ["linear"], ["zoom"], 16, 3.5, 20, 8],
        "line-opacity": 0.92,
        "line-gradient": [
          "interpolate", ["linear"], ["line-progress"],
          0, "#2c5cbe",
          0.5, "#ffc457",
          1, "#e7583f"
        ]
      }
    }, beforeCat);
  }
  if (!map.getLayer(MOVEMENT_POINT_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_POINT_LAYER_ID,
      type: "circle",
      source: MOVEMENT_SOURCE_ID,
      filter: ["==", ["get", "kind"], "sample"],
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 17, 2.5, 20, 5],
        "circle-color": "#f8fbf6",
        "circle-stroke-color": "#e7583f",
        "circle-stroke-width": 1.2,
        "circle-opacity": 0.75
      }
    }, beforeCat);
  }
  if (!map.getLayer(MOVEMENT_ENDPOINT_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_ENDPOINT_LAYER_ID,
      type: "circle",
      source: MOVEMENT_SOURCE_ID,
      filter: ["in", ["get", "kind"], ["literal", ["start", "end"]]],
      paint: {
        "circle-radius": ["match", ["get", "kind"], "start", 10, "end", 13, 10],
        "circle-color": ["match", ["get", "kind"], "start", "#2c5cbe", "end", "#e7583f", "#2f6f5d"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 3
      }
    }, beforeCat);
  }
  if (!map.getLayer(MOVEMENT_ENDPOINT_LABEL_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_ENDPOINT_LABEL_LAYER_ID,
      type: "symbol",
      source: MOVEMENT_SOURCE_ID,
      filter: ["in", ["get", "kind"], ["literal", ["start", "end"]]],
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 11,
        "text-offset": [0, 1.7],
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#18211c",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.5
      }
    }, beforeCat);
  }
  if (!map.getLayer(MOVEMENT_ARROW_LAYER_ID)) {
    map.addLayer({
      id: MOVEMENT_ARROW_LAYER_ID,
      type: "symbol",
      source: MOVEMENT_ARROW_SOURCE_ID,
      layout: {
        "text-field": ">",
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 17, 13, 20, 18],
        "text-rotate": ["get", "bearing"],
        "text-rotation-alignment": "map",
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#18211c",
        "text-halo-color": "#ffc457",
        "text-halo-width": 1.2,
        "text-opacity": 0.85
      }
    }, beforeCat);
  }
}

function ensureDrawingLayers(map: Map) {
  if (!map.getSource(DRAWING_LINE_SOURCE_ID)) {
    map.addSource(DRAWING_LINE_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getSource(DRAWING_FILL_SOURCE_ID)) {
    map.addSource(DRAWING_FILL_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getSource(DRAWING_TEXT_SOURCE_ID)) {
    map.addSource(DRAWING_TEXT_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  if (!map.getSource(DRAWING_PREVIEW_SOURCE_ID)) {
    map.addSource(DRAWING_PREVIEW_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  const beforeCat = map.getLayer(CAT_ICON_LAYER_ID) ? CAT_ICON_LAYER_ID : undefined;
  if (!map.getLayer(DRAWING_FILL_LAYER_ID)) {
    map.addLayer({
      id: DRAWING_FILL_LAYER_ID,
      type: "fill",
      source: DRAWING_FILL_SOURCE_ID,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.18
      }
    }, beforeCat);
  }
  if (!map.getLayer(DRAWING_LINE_LAYER_ID)) {
    map.addLayer({
      id: DRAWING_LINE_LAYER_ID,
      type: "line",
      source: DRAWING_LINE_SOURCE_ID,
      layout: {
        "line-cap": "round",
        "line-join": "round"
      },
      paint: {
        "line-color": ["get", "color"],
        "line-width": ["interpolate", ["linear"], ["zoom"], 16, 3, 20, 7],
        "line-opacity": 0.94
      }
    }, beforeCat);
  }
  if (!map.getLayer(DRAWING_TEXT_LAYER_ID)) {
    map.addLayer({
      id: DRAWING_TEXT_LAYER_ID,
      type: "symbol",
      source: DRAWING_TEXT_SOURCE_ID,
      layout: {
        "text-field": ["get", "text"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 16, 13, 20, 20],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": ["get", "color"],
        "text-halo-color": "#ffffff",
        "text-halo-width": 2
      }
    }, beforeCat);
  }
  if (!map.getLayer(DRAWING_PREVIEW_FILL_LAYER_ID)) {
    map.addLayer({
      id: DRAWING_PREVIEW_FILL_LAYER_ID,
      type: "fill",
      source: DRAWING_PREVIEW_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.13
      }
    }, beforeCat);
  }
  if (!map.getLayer(DRAWING_PREVIEW_LINE_LAYER_ID)) {
    map.addLayer({
      id: DRAWING_PREVIEW_LINE_LAYER_ID,
      type: "line",
      source: DRAWING_PREVIEW_SOURCE_ID,
      layout: {
        "line-cap": "round",
        "line-join": "round"
      },
      paint: {
        "line-color": ["get", "color"],
        "line-width": 3,
        "line-dasharray": [0.5, 1.4],
        "line-opacity": 0.85
      }
    }, beforeCat);
  }
}

function alertAreaFeatureCollection(areas: AlertArea[]) {
  const features: object[] = [];
  areas.forEach((area) => {
    if (!area.polygon || area.polygon.length < 3) return;
    const ring = ensureClosedPolygon(area.polygon);
    const properties = {
      id: area.id,
      name: area.name,
      color: area.color,
      enabled: area.enabled,
      conditionMet: area.conditionMet === true
    };
    features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties
    });
  });
  return { type: "FeatureCollection", features };
}

function ensureAlertAreaLayers(map: Map) {
  if (!map.getSource(ALERT_AREA_SOURCE_ID)) {
    map.addSource(ALERT_AREA_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection as never });
  }
  // Keep alert areas below the drawing/cat layers so free-hand annotations and
  // the cat marker always stay on top.
  const beforeId =
    (map.getLayer(DRAWING_FILL_LAYER_ID) && DRAWING_FILL_LAYER_ID) ||
    (map.getLayer(CAT_ICON_LAYER_ID) && CAT_ICON_LAYER_ID) ||
    undefined;
  if (!map.getLayer(ALERT_AREA_FILL_LAYER_ID)) {
    map.addLayer({
      id: ALERT_AREA_FILL_LAYER_ID,
      type: "fill",
      source: ALERT_AREA_SOURCE_ID,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": ["case", ["boolean", ["get", "enabled"], true], 0.16, 0.05]
      }
    }, beforeId || undefined);
  }
  if (!map.getLayer(ALERT_AREA_LINE_LAYER_ID)) {
    map.addLayer({
      id: ALERT_AREA_LINE_LAYER_ID,
      type: "line",
      source: ALERT_AREA_SOURCE_ID,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": ["get", "color"],
        "line-width": ["interpolate", ["linear"], ["zoom"], 16, 2, 20, 4],
        "line-opacity": ["case", ["boolean", ["get", "enabled"], true], 0.9, 0.4],
        "line-dasharray": [2, 1.2]
      }
    }, beforeId || undefined);
  }
  if (!map.getLayer(ALERT_AREA_LABEL_LAYER_ID)) {
    map.addLayer({
      id: ALERT_AREA_LABEL_LAYER_ID,
      type: "symbol",
      source: ALERT_AREA_SOURCE_ID,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 16, 11, 20, 16]
      },
      paint: {
        "text-color": ["get", "color"],
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.8
      }
    }, beforeId || undefined);
  }
}

function friendlyHttpError(status: number, body: string): string {
  let detail = "";
  try {
    detail = JSON.parse(body)?.detail ?? "";
  } catch {
    detail = body;
  }
  if (detail === "database_unavailable") {
    return "Server-Datenbank vorübergehend nicht erreichbar – neuer Versuch läuft automatisch…";
  }
  if (status >= 500) {
    return `Server momentan nicht erreichbar (${status}) – neuer Versuch läuft automatisch…`;
  }
  return detail || `Fehler ${status}`;
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = { "Content-Type": "application/json", ...(init?.headers || {}) };
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    cache: init?.cache || "no-store",
    headers
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(friendlyHttpError(response.status, text || response.statusText));
  }
  return response.json() as Promise<T>;
}

// MAC suffix (last 6 hex of the room id, e.g. siegi_2cef30 -> "2cef30") -> board
// number, for all 15 boards. Keep in sync with the board registry in AGENTS.md.
const BOARD_SUFFIX_TO_NUMBER: Record<string, number> = {
  f8c416: 1, "2c44b9": 2, "8ce06b": 3, c0e885: 4, b0d685: 5,
  "04e985": 6, "40e785": 7, "2cef30": 8, "70bf30": 9, "801331": 10,
  "50eb31": 11, "1c6b33": 12, b87405: 13, "449f06": 14, "7060e8": 15
};

function receiverBoardNumber(receiver: Pick<Receiver, "id" | "room" | "name"> | null | undefined) {
  if (!receiver) return null;
  const source = `${receiver.name} ${receiver.id} ${receiver.room}`;
  const boardMatch = source.match(/board\s*(\d+)/i);
  if (boardMatch) return parseInt(boardMatch[1], 10);
  for (const [suffix, num] of Object.entries(BOARD_SUFFIX_TO_NUMBER)) {
    if (receiver.id.includes(suffix) || receiver.room.includes(suffix)) return num;
  }
  return null;
}

function receiverShortLabel(receiver: Pick<Receiver, "id" | "room" | "name"> | null | undefined) {
  if (!receiver) return "B?";
  const num = receiverBoardNumber(receiver);
  if (num != null) return `B${num}`;
  return receiver.name.slice(0, 2).toUpperCase();
}

function receiverSortKey(receiver: Pick<Receiver, "id" | "room" | "name"> | null | undefined) {
  return receiverBoardNumber(receiver) ?? 9999;
}

function trainingWalkLabel(walk: TrainingWalk) {
  if (!walk.session) return `${walk.samples.length} Punkte`;
  const started = new Date(walk.session.startedAt).toLocaleString("de-DE", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
  return `${walk.samples.length} Punkte vom ${started}`;
}

function fitTrainingWalkBounds(map: Map, samples: TrainingWalkSample[]) {
  const first = samples[0];
  if (!first) return;
  if (samples.length === 1) {
    map.flyTo({ center: [first.lon, first.lat], zoom: 20, duration: 600 });
    return;
  }
  const bounds = new mapboxgl.LngLatBounds([first.lon, first.lat], [first.lon, first.lat]);
  for (const sample of samples.slice(1)) {
    bounds.extend([sample.lon, sample.lat]);
  }
  map.fitBounds(bounds, {
    padding: { top: 180, bottom: 135, left: 55, right: 55 },
    maxZoom: 20,
    duration: 650
  });
}

function SignalBars({ values }: { values: Array<number | null | undefined> }) {
  const recent = values.slice(-36);
  return (
    <div className="walk-bars" aria-hidden="true">
      {recent.map((value, index) => {
        const height = value == null ? 5 : Math.max(5, Math.min(100, ((value + 100) / 65) * 100));
        return <span key={index} className={value == null ? "empty" : ""} style={{ height: `${height}%` }} />;
      })}
    </div>
  );
}

function WalkMetricCard({
  title,
  icon,
  value,
  detail,
  quality,
  children
}: {
  title: string;
  icon: React.ReactNode;
  value: string;
  detail: string;
  quality: { label: string; className: string };
  children: React.ReactNode;
}) {
  return (
    <section className={`walk-card ${quality.className}`}>
      <header>
        <span>{icon}{title}</span>
        <strong>{quality.label}</strong>
      </header>
      <div className="walk-value">{value}</div>
      <p>{detail}</p>
      {children}
    </section>
  );
}

function WalkTestApp() {
  const boardKey = window.location.pathname.split("/").filter(Boolean)[1] || "board-1";
  const [data, setData] = useState<WalkTestState | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const loadWalkState = useCallback(async () => {
    try {
      const response = await fetchJson<WalkTestState>(`/api/walk-test/${boardKey}`);
      setData(response);
      setLastError(null);
    } catch (error) {
      setLastError(error instanceof Error ? error.message : String(error));
    }
  }, [boardKey]);

  useEffect(() => {
    loadWalkState();
    const interval = window.setInterval(loadWalkState, 3000);
    return () => window.clearInterval(interval);
  }, [loadWalkState]);

  const wifiQuality = signalQuality(data?.wifi?.wifiRssi, "wifi");
  const bleQuality = signalQuality(data?.ble?.rssi, "ble");
  const mqttConnected = data?.mqtt?.subscriber_connected;

  return (
    <main className="walk-shell">
      <section className="walk-header">
        <div>
          <div className="eyebrow"><Activity size={15} /> Walk Test</div>
          <h1>{data?.board.label || "Board"}</h1>
          <p>{data?.board.hardware || "—"} · {data?.board.usbPort || "—"}</p>
        </div>
        <button className="icon-button" aria-label="Refresh" onClick={loadWalkState}>
          <RefreshCw size={20} />
        </button>
      </section>

      <section className="walk-grid">
        <WalkMetricCard
          title="WiFi"
          icon={<Wifi size={18} />}
          value={formatDbm(data?.wifi?.wifiRssi)}
          detail={data?.wifi ? `${formatAgo(data.wifi.observedAt)} old · ${data.wifi.ip || "no IP"}` : "No telemetry yet"}
          quality={wifiQuality}
        >
          <SignalBars values={(data?.history.wifi || []).map((item) => item.wifiRssi)} />
        </WalkMetricCard>

        <WalkMetricCard
          title="BLE Beacon"
          icon={<Radio size={18} />}
          value={formatDbm(data?.ble?.rssi)}
          detail={data?.ble ? `${formatAgo(data.ble.observedAt)} old · ${formatDistance(data.ble.distanceM)}` : "No beacon signal yet"}
          quality={bleQuality}
        >
          <SignalBars values={(data?.history.ble || []).map((item) => item.rssi)} />
        </WalkMetricCard>
      </section>

      <section className="walk-details">
        <dl>
          <div><dt>Room</dt><dd>{data?.board.room || "—"}</dd></div>
          <div><dt>Beacon</dt><dd>{data?.beacon.id || "—"}</dd></div>
          <div><dt>MQTT</dt><dd>{mqttConnected ? "connected" : "waiting"}</dd></div>
          <div><dt>Firmware</dt><dd>{data?.wifi?.firmware || data?.board.firmware || "-"}</dd></div>
          <div><dt>Uptime</dt><dd>{data?.wifi?.uptimeS == null ? "-" : `${Math.round(data.wifi.uptimeS / 60)} min`}</dd></div>
          <div><dt>Samples</dt><dd>{(data?.history.wifi.length || 0)} WiFi · {(data?.history.ble.length || 0)} BLE</dd></div>
        </dl>
      </section>

      {lastError ? (
        <div className="walk-error">
          <RefreshCw size={15} />
          {lastError}
        </div>
      ) : null}
    </main>
  );
}

function AvailabilityChart({ data }: { data: AvailabilityResponse }) {
  const windowStart = new Date(data.windowStart);
  const windowEnd = new Date(data.windowEnd);
  const totalMs = windowEnd.getTime() - windowStart.getTime();
  const tickHours = data.range === "7d" ? 24 : 4;
  const ticks: { x: number; label: string }[] = [];
  const tickMs = tickHours * 3600 * 1000;
  const firstTick = Math.ceil(windowStart.getTime() / tickMs) * tickMs;
  for (let t = firstTick; t <= windowEnd.getTime(); t += tickMs) {
    const d = new Date(t);
    const label = data.range === "7d"
      ? d.toLocaleDateString(undefined, { weekday: "short" })
      : `${d.getHours().toString().padStart(2, "0")}:00`;
    ticks.push({ x: (t - windowStart.getTime()) / totalMs, label });
  }
  return (
    <div className="availability-chart">
      <div className="availability-ticks">
        {ticks.map((t, i) => (
          <span key={i} className="availability-tick" style={{ left: `${t.x * 100}%` }}>{t.label}</span>
        ))}
      </div>
      {data.boards.map((board) => (
        <div key={board.boardKey} className="availability-row">
          <div className="availability-row-label">
            <strong>{board.label}</strong>
            <span>{board.uptimePct.toFixed(1)}%</span>
          </div>
          <div className="availability-row-track" title={`${board.uptimePct.toFixed(1)}% online · ${board.sampleCount} samples`}>
            {board.buckets.map((coverage, idx) => {
              const left = (idx / board.buckets.length) * 100;
              const width = 100 / board.buckets.length;
              const opacity = coverage;
              return (
                <span
                  key={idx}
                  className="availability-bucket"
                  style={{ left: `${left}%`, width: `${width}%`, opacity: coverage === 0 ? 0 : Math.max(0.15, opacity) }}
                />
              );
            })}
          </div>
        </div>
      ))}
      <div className="availability-legend">
        <span><i className="legend-on" /> online</span>
        <span><i className="legend-off" /> offline</span>
        <span className="small">Bucket: {Math.round(data.bucketSeconds / 60)} min · gap threshold: {data.gapThresholdSeconds}s</span>
      </div>
    </div>
  );
}

function SignalMeter({ value, kind, label }: { value: number | null | undefined; kind: "wifi" | "ble"; label: string }) {
  const percent = signalPercent(value, kind);
  const quality = signalQuality(value, kind);
  return (
    <span className="signal-meter">
      <span className="signal-meter-top">
        <span>{label}</span>
        <strong>{formatDbm(value)}</strong>
      </span>
      <span className="signal-meter-track" aria-hidden="true">
        <span className={`signal-meter-fill ${quality.className}`} style={{ width: `${percent}%` }} />
      </span>
    </span>
  );
}

function FreshnessBadge({ lastUpdatedAt, hasError }: { lastUpdatedAt: number | null; hasError: boolean }) {
  const ageSeconds = lastUpdatedAt ? Math.round((Date.now() - lastUpdatedAt) / 1000) : null;
  const stale = !lastUpdatedAt || ageSeconds == null || ageSeconds > STALE_DATA_SECONDS || hasError;
  return (
    <span className={`freshness-badge ${stale ? "stale" : "fresh"}`}>
      <RefreshCw size={13} />
      {stale
        ? lastUpdatedAt
          ? `Keine frischen Daten – letzter Abruf vor ${ageSeconds} s`
          : "Warte auf Live-Daten"
        : `Aktualisiert vor ${ageSeconds} s`}
    </span>
  );
}

function InstallToast({
  visible,
  ios,
  onInstall,
  onDismiss
}: {
  visible: boolean;
  ios: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}) {
  if (!visible) return null;
  return (
    <section className="install-toast">
      <div>
        <strong>Siegi Locator installieren</strong>
        <p>{ios ? "Tippe auf das Teilen-Symbol, dann „Zum Home-Bildschirm“." : "Die Live-Karte als App auf diesem Gerät – immer einen Tipp entfernt."}</p>
      </div>
      {ios ? null : (
        <button className="primary-action" onClick={onInstall}>
          <Download size={16} /> Installieren
        </button>
      )}
      <button className="icon-button ghost" aria-label="Installationshinweis schließen" onClick={onDismiss}>
        <X size={18} />
      </button>
    </section>
  );
}

function CollapsibleSection({
  title,
  icon,
  open,
  onToggle,
  children,
  className = ""
}: {
  title: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`drawer-section collapsible-section ${className} ${open ? "open" : ""}`}>
      <button className="section-toggle" type="button" onClick={onToggle} aria-expanded={open}>
        <span>{icon}{title}</span>
        <ChevronDown size={17} />
      </button>
      {open ? <div className="section-body">{children}</div> : null}
    </section>
  );
}

function App() {
  const [config, setConfig] = useState<Config | null>(null);
  const [appState, setAppState] = useState<AppState | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [receiverEditMode, setReceiverEditMode] = useState(false);
  const [receiverSaveMessage, setReceiverSaveMessage] = useState("");
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [trainingSessionId, setTrainingSessionId] = useState<string | null>(null);
  const [trainingMode, setTrainingMode] = useState<"single" | "cumulative">("single");
  const [trainingBusy, setTrainingBusy] = useState(false);
  const [trainingMessage, setTrainingMessage] = useState("");
  const [trainingWalk, setTrainingWalk] = useState<TrainingWalk | null>(null);
  const [trainingWalkVisible, setTrainingWalkVisible] = useState(false);
  const [trainingWalkBusy, setTrainingWalkBusy] = useState(false);
  const [trainingWalkMessage, setTrainingWalkMessage] = useState("");
  const [trainingSummary, setTrainingSummary] = useState<TrainingSummary | null>(null);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityRange, setAvailabilityRange] = useState<"24h" | "7d">("24h");
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastStateFetchedAt, setLastStateFetchedAt] = useState<number | null>(null);
  const [, setNowTick] = useState(Date.now());
  const [movementRange, setMovementRange] = useState<MovementRange>("60m");
  const [movementMode, setMovementMode] = useState<MovementMode>(null);
  const [movementHistory, setMovementHistory] = useState<PositionHistoryResponse | null>(null);
  const [movementBusy, setMovementBusy] = useState(false);
  const [movementMessage, setMovementMessage] = useState("");
  const [drawingMode, setDrawingMode] = useState<DrawingMode>("select");
  const [drawingColor, setDrawingColor] = useState(DEFAULT_DRAWING_COLOR);
  const [drawings, setDrawings] = useState<DrawingFeature[]>([]);
  const [drawingDraft, setDrawingDraft] = useState<Array<[number, number]>>([]);
  const [drawingHover, setDrawingHover] = useState<[number, number] | null>(null);
  const [drawingMessage, setDrawingMessage] = useState("");
  const [alertAreaDrawing, setAlertAreaDrawing] = useState(false);
  const [alertAreaForm, setAlertAreaForm] = useState<{
    editingId: string | null;
    polygon: Array<[number, number]>;
    name: string;
    direction: AlertDirection;
    notifyMode: AlertNotifyMode;
    buttonText: string;
    color: string;
  } | null>(null);
  const [alertAreaMessage, setAlertAreaMessage] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => typeof Notification !== "undefined" && Notification.permission === "granted"
  );
  const [notificationMessage, setNotificationMessage] = useState("");
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installToastVisible, setInstallToastVisible] = useState(false);
  const [installMessage, setInstallMessage] = useState("");
  const [iosInstallHint, setIosInstallHint] = useState(false);
  const [standaloneInstalled, setStandaloneInstalled] = useState(() => isStandaloneApp());
  const [appUpdateMessage, setAppUpdateMessage] = useState("");
  const [openSettingsSections, setOpenSettingsSections] = useState<Record<SettingsSectionKey, boolean>>({
    movement: true,
    alerts: true,
    annotations: false,
    notifications: false,
    install: false,
    mqtt: false,
    model: false,
    training: false,
    receivers: false,
    diagnostics: false
  });
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const receiverDragOverridesRef = useRef<ReceiverDragOverrides>({});
  const userLocationRef = useRef<UserLocation | null>(null);
  const trainingSessionIdRef = useRef<string | null>(null);
  const appStateRef = useRef<AppState | null>(null);
  const drawingColorRef = useRef(DEFAULT_DRAWING_COLOR);
  const drawingDraftRef = useRef<Array<[number, number]>>([]);
  const drawingsRef = useRef<DrawingFeature[]>([]);
  const alertAreaDrawingRef = useRef(false);
  const pendingAlertMetaRef = useRef<{
    editingId: string | null;
    name: string;
    direction: AlertDirection;
    notifyMode: AlertNotifyMode;
    buttonText: string;
    color: string;
  } | null>(null);
  const movementModeRef = useRef<MovementMode>(null);
  const pushSyncedRef = useRef(false);
  const pushEndpointRef = useRef<string | null>(null);

  const loadState = useCallback(async () => {
    try {
      const endpoint = pushEndpointRef.current;
      const data = await fetchJson<AppState>(
        `/api/state${endpoint ? `?pushEndpoint=${encodeURIComponent(endpoint)}` : ""}`
      );
      setAppState(data);
      setLastStateFetchedAt(Date.now());
      setLastError(null);
    } catch (error) {
      setLastError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const loadTrainingSummary = useCallback(async () => {
    try {
      const summary = await fetchJson<TrainingSummary>("/api/training/summary");
      setTrainingSummary(summary);
    } catch (error) {
      setTrainingSummary(null);
    }
  }, []);

  const loadAvailability = useCallback(
    async (range: "24h" | "7d") => {
      setAvailabilityBusy(true);
      setAvailabilityMessage("");
      try {
        const data = await fetchJson<AvailabilityResponse>(`/api/diagnostics/availability?range=${range}`);
        setAvailability(data);
      } catch (error) {
        setAvailabilityMessage(error instanceof Error ? error.message : String(error));
      } finally {
        setAvailabilityBusy(false);
      }
    },
    []
  );

  const loadMovementHistory = useCallback(
    async (range: MovementRange = movementRange, mode: MovementMode = movementModeRef.current) => {
      if (!mode) return;
      setMovementBusy(true);
      setMovementMessage("");
      try {
        const data = await fetchJson<PositionHistoryResponse>(`/api/positions/history?range=${range}`);
        setMovementHistory(data);
        const count = data.points.length;
        setMovementMessage(
          count > 0
            ? `${mode === "trail" ? "Route" : "Heatmap"}: ${count} Positionen in ${movementRangeLabel(data.range)}${data.limited ? " (begrenzt)" : ""}`
            : `Keine Positionen in den letzten ${movementRangeLabel(data.range)} gespeichert`
        );
      } catch (error) {
        setMovementMessage(error instanceof Error ? error.message : String(error));
      } finally {
        setMovementBusy(false);
      }
    },
    [movementRange]
  );

  const resetTrainingData = useCallback(async () => {
    if (!window.confirm("Alle Trainingsdaten zurücksetzen und das aktive Modell deaktivieren?")) return;
    setTrainingBusy(true);
    try {
      const response = await fetchJson<{ deletedSamples: number; deletedSessions: number }>(
        "/api/training/reset",
        { method: "POST" }
      );
      setTrainingMessage(`Zurückgesetzt: ${response.deletedSamples} Punkte, ${response.deletedSessions} Sessions gelöscht.`);
      await Promise.all([loadState(), loadTrainingSummary()]);
    } catch (error) {
      setTrainingMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setTrainingBusy(false);
    }
  }, [loadState, loadTrainingSummary]);

  useEffect(() => {
    fetchJson<Config>("/api/config")
      .then((data) => setConfig({ ...data, mapboxToken: data.mapboxToken || buildToken }))
      .catch((error) => setLastError(error instanceof Error ? error.message : String(error)));
    loadState();
    const interval = window.setInterval(loadState, STATE_POLL_MS);
    return () => window.clearInterval(interval);
  }, [loadState]);

  useEffect(() => {
    if (!drawerOpen) return;
    loadTrainingSummary();
    loadAvailability(availabilityRange);
    const interval = window.setInterval(() => {
      loadTrainingSummary();
      loadAvailability(availabilityRange);
    }, SETTINGS_POLL_MS);
    return () => window.clearInterval(interval);
  }, [drawerOpen, availabilityRange, loadTrainingSummary, loadAvailability]);

  useEffect(() => {
    if (!movementMode) return;
    loadMovementHistory(movementRange, movementMode);
    const interval = window.setInterval(() => loadMovementHistory(movementRange, movementMode), SETTINGS_POLL_MS);
    return () => window.clearInterval(interval);
  }, [loadMovementHistory, movementMode, movementRange]);

  useEffect(() => {
    const interval = window.setInterval(() => setNowTick(Date.now()), 1000);
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") {
        setNowTick(Date.now());
        loadState();
      }
    };
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [loadState]);

  useEffect(() => {
    registerFreshServiceWorker(() => setAppUpdateMessage("Neue Version gefunden – wird aktiviert."));
  }, []);

  // Keep the installed-state in sync with reality so we never nag an installed
  // user. Three independent signals: the display-mode media query (covers
  // launching the installed PWA), getInstalledRelatedApps (Chromium), and the
  // appinstalled event.
  useEffect(() => {
    let cancelled = false;
    const mql = window.matchMedia("(display-mode: standalone)");
    const syncStandalone = () => {
      if (isStandaloneApp()) {
        setStandaloneInstalled(true);
        setInstallToastVisible(false);
      }
    };
    syncStandalone();
    mql.addEventListener?.("change", syncStandalone);
    const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
    if (typeof nav.getInstalledRelatedApps === "function") {
      nav
        .getInstalledRelatedApps()
        .then((apps) => {
          if (!cancelled && Array.isArray(apps) && apps.length > 0) {
            setStandaloneInstalled(true);
            setInstallToastVisible(false);
          }
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
      mql.removeEventListener?.("change", syncStandalone);
    };
  }, []);

  // iOS Safari never fires beforeinstallprompt, so offer a manual hint there —
  // but only when not already running standalone.
  useEffect(() => {
    if (standaloneInstalled || isStandaloneApp()) return;
    if (isIosSafari() && isMobileInstallSurface() && !installPromptSnoozed()) {
      setIosInstallHint(true);
      setInstallToastVisible(true);
    }
  }, [standaloneInstalled]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      setDeferredInstallPrompt(promptEvent);
      setInstallMessage("");
      // beforeinstallprompt only fires when the app is installable AND not yet
      // installed, so this is a reliable signal that the toast is warranted.
      if (!standaloneInstalled && !isStandaloneApp() && isMobileInstallSurface() && !installPromptSnoozed()) {
        setInstallToastVisible(true);
      }
    };
    const onInstalled = () => {
      setStandaloneInstalled(true);
      setInstallToastVisible(false);
      setDeferredInstallPrompt(null);
      setInstallMessage("Auf diesem Gerät installiert.");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [standaloneInstalled]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy ?? null,
          heading: position.coords.heading ?? null
        });
      },
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    trainingSessionIdRef.current = trainingSessionId;
  }, [trainingSessionId]);

  useEffect(() => {
    if (!config || !mapContainerRef.current || mapRef.current || !config.mapboxToken) return;
    mapboxgl.accessToken = config.mapboxToken;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/outdoors-v12",
      center: [config.home.lon, config.home.lat],
      zoom: 18,
      pitch: 0,
      bearing: 0,
      preserveDrawingBuffer: true,
      attributionControl: false
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    map.addControl(new mapboxgl.NavigationControl({ showCompass: true, showZoom: true }), "top-right");
    map.once("load", () => {
      ensurePositionLayers(map);
      ensureMovementLayers(map);
      ensureDrawingLayers(map);
      ensureAlertAreaLayers(map);
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [config]);

  const saveReceiverPosition = useCallback(async (receiver: Receiver, lat: number, lon: number) => {
    const label = receiverShortLabel(receiver);
    setReceiverSaveMessage(`Speichere ${label}…`);
    await fetchJson<Receiver>("/api/receivers", {
      method: "POST",
      body: JSON.stringify({
        id: receiver.id,
        room: receiver.room,
        name: receiver.name,
        lat,
        lon,
        heightM: receiver.heightM,
        placement: receiver.placement,
        isActive: receiver.isActive
      })
    });
    setReceiverSaveMessage(`${label} gespeichert`);
    await loadState();
  }, [loadState]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    drawingColorRef.current = drawingColor;
  }, [drawingColor]);

  useEffect(() => {
    drawingDraftRef.current = drawingDraft;
  }, [drawingDraft]);

  useEffect(() => {
    drawingsRef.current = drawings;
  }, [drawings]);

  useEffect(() => {
    alertAreaDrawingRef.current = alertAreaDrawing;
  }, [alertAreaDrawing]);

  // Load annotations from the server (shared across devices), migrating any
  // drawings still sitting in this browser's old localStorage on first run.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const serverDrawings = await fetchJson<DrawingFeature[]>("/api/annotations");
        if (cancelled) return;
        const legacy = loadLegacyLocalDrawings();
        if (serverDrawings.length === 0 && legacy.length > 0) {
          for (const drawing of legacy) {
            try {
              await fetchJson<DrawingFeature>("/api/annotations", { method: "POST", body: JSON.stringify(drawing) });
            } catch {
              /* best-effort migration */
            }
          }
          window.localStorage.removeItem(DRAWING_STORAGE_KEY);
          if (!cancelled) setDrawings(legacy);
        } else {
          if (legacy.length > 0) window.localStorage.removeItem(DRAWING_STORAGE_KEY);
          setDrawings(serverDrawings);
        }
      } catch (error) {
        if (!cancelled) setDrawingMessage(error instanceof Error ? error.message : String(error));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    movementModeRef.current = movementMode;
  }, [movementMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();
    const drawingActive = drawingMode !== "select";
    if (drawingActive) {
      map.dragPan.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      canvas.style.cursor = drawingMode === "eyedropper" ? "copy" : drawingMode === "erase" ? "not-allowed" : "crosshair";
      return () => {
        map.dragPan.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        canvas.style.cursor = "";
      };
    }
    canvas.style.cursor = "";
  }, [drawingMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !appState) return;

    const syncMap = () => {
      ensurePositionLayers(map);
      setGeoJsonSourceData(map, CAT_SOURCE_ID, catFeatureCollection(appState.estimate));
      setGeoJsonSourceData(
        map,
        RECEIVER_SOURCE_ID,
        receiverFeatureCollection(appState, receiverEditMode, receiverDragOverridesRef.current)
      );
      // Render alert areas here (driven by the same live appState as the cat) so
      // they refresh on every poll, not only on a full page reload.
      ensureAlertAreaLayers(map);
      setGeoJsonSourceData(map, ALERT_AREA_SOURCE_ID, alertAreaFeatureCollection(appState.alertAreas || []));
    };

    if (map.loaded()) {
      syncMap();
    } else {
      map.once("load", syncMap);
    }
  }, [appState, receiverEditMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !receiverEditMode) return;

    let dragId: string | null = null;
    let dragReceiver: Receiver | null = null;
    const canvas = map.getCanvas();

    const refreshReceiverSource = () => {
      const state = appStateRef.current;
      if (!state) return;
      setGeoJsonSourceData(map, RECEIVER_SOURCE_ID, receiverFeatureCollection(state, true, receiverDragOverridesRef.current));
    };

    const onLayerEnter = () => {
      if (!dragId) canvas.style.cursor = "grab";
    };
    const onLayerLeave = () => {
      if (!dragId) canvas.style.cursor = "";
    };
    const onLayerDown = (e: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const id = feature.properties?.id as string | undefined;
      if (!id) return;
      const state = appStateRef.current;
      const receiver = state?.receivers.find((item) => item.id === id) || null;
      if (!receiver) return;
      e.preventDefault();
      dragId = id;
      dragReceiver = receiver;
      receiverDragOverridesRef.current[id] = { lat: e.lngLat.lat, lon: e.lngLat.lng };
      canvas.style.cursor = "grabbing";
      refreshReceiverSource();
    };
    const onMapMove = (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      if (!dragId) return;
      receiverDragOverridesRef.current[dragId] = { lat: e.lngLat.lat, lon: e.lngLat.lng };
      refreshReceiverSource();
    };
    const finalizeDrag = (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      if (!dragId || !dragReceiver) return;
      const id = dragId;
      const receiver = dragReceiver;
      const next = e.lngLat;
      dragId = null;
      dragReceiver = null;
      canvas.style.cursor = "";
      saveReceiverPosition(receiver, next.lat, next.lng)
        .catch((error) => {
          setReceiverSaveMessage(error instanceof Error ? error.message : String(error));
        })
        .finally(() => {
          delete receiverDragOverridesRef.current[id];
          refreshReceiverSource();
        });
    };

    map.on("mouseenter", RECEIVER_CIRCLE_LAYER_ID, onLayerEnter);
    map.on("mouseleave", RECEIVER_CIRCLE_LAYER_ID, onLayerLeave);
    map.on("mousedown", RECEIVER_CIRCLE_LAYER_ID, onLayerDown);
    map.on("touchstart", RECEIVER_CIRCLE_LAYER_ID, onLayerDown);
    map.on("mousemove", onMapMove);
    map.on("touchmove", onMapMove);
    map.on("mouseup", finalizeDrag);
    map.on("touchend", finalizeDrag);

    return () => {
      map.off("mouseenter", RECEIVER_CIRCLE_LAYER_ID, onLayerEnter);
      map.off("mouseleave", RECEIVER_CIRCLE_LAYER_ID, onLayerLeave);
      map.off("mousedown", RECEIVER_CIRCLE_LAYER_ID, onLayerDown);
      map.off("touchstart", RECEIVER_CIRCLE_LAYER_ID, onLayerDown);
      map.off("mousemove", onMapMove);
      map.off("touchmove", onMapMove);
      map.off("mouseup", finalizeDrag);
      map.off("touchend", finalizeDrag);
      canvas.style.cursor = "";
    };
  }, [receiverEditMode, saveReceiverPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const syncTrainingWalk = () => {
      ensureTrainingWalkLayers(map);
      setGeoJsonSourceData(
        map,
        TRAINING_WALK_SOURCE_ID,
        trainingWalkFeatureCollection(trainingWalk, trainingWalkVisible)
      );
    };
    if (map.loaded()) {
      syncTrainingWalk();
    } else {
      map.once("load", syncTrainingWalk);
    }
  }, [trainingWalk, trainingWalkVisible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const syncMovement = () => {
      ensureMovementLayers(map);
      setGeoJsonSourceData(map, MOVEMENT_SOURCE_ID, movementFeatureCollection(movementHistory, movementMode));
      setGeoJsonSourceData(map, MOVEMENT_ARROW_SOURCE_ID, movementArrowFeatureCollection(movementHistory, movementMode));
      setGeoJsonSourceData(map, MOVEMENT_HEATMAP_SOURCE_ID, movementHeatmapFeatureCollection(movementHistory, movementMode));
    };
    if (map.loaded()) {
      syncMovement();
    } else {
      map.once("load", syncMovement);
    }
  }, [movementHistory, movementMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const syncDrawings = () => {
      ensureDrawingLayers(map);
      const collections = drawingFeatureCollections(drawings);
      setGeoJsonSourceData(map, DRAWING_LINE_SOURCE_ID, collections.lines);
      setGeoJsonSourceData(map, DRAWING_FILL_SOURCE_ID, collections.fills);
      setGeoJsonSourceData(map, DRAWING_TEXT_SOURCE_ID, collections.texts);
      setGeoJsonSourceData(
        map,
        DRAWING_PREVIEW_SOURCE_ID,
        drawingPreviewFeatureCollection(drawingMode, drawingDraft, drawingHover, drawingColor)
      );
    };
    if (map.loaded()) {
      syncDrawings();
    } else {
      map.once("load", syncDrawings);
    }
  }, [drawingColor, drawingDraft, drawingHover, drawingMode, drawings]);

  const referenceLocation = userLocation || (config ? { lat: config.home.lat, lon: config.home.lon } : null);
  const navigation = useMemo(() => {
    if (!referenceLocation || !appState) return null;
    const target = { lat: appState.estimate.lat, lon: appState.estimate.lon };
    const distance = haversineMeters(referenceLocation, target);
    const bearing = bearingDegrees(referenceLocation, target);
    const relative = (bearing - (userLocation?.heading ?? 0) + 360) % 360;
    return { distance, bearing, relative };
  }, [appState, referenceLocation, userLocation?.heading]);

  const focusTrainingWalk = useCallback((walkOverride?: TrainingWalk | null) => {
    const walk = walkOverride ?? trainingWalk;
    const map = mapRef.current;
    if (!map || !walk || walk.samples.length === 0) return;
    fitTrainingWalkBounds(map, walk.samples);
  }, [trainingWalk]);

  const focusMovement = useCallback((historyOverride?: PositionHistoryResponse | null) => {
    const history = historyOverride ?? movementHistory;
    const map = mapRef.current;
    if (!map || !history || history.points.length === 0) return;
    const first = history.points[0];
    if (history.points.length === 1) {
      map.flyTo({ center: [first.lon, first.lat], zoom: 20, duration: 600 });
      return;
    }
    const bounds = new mapboxgl.LngLatBounds([first.lon, first.lat], [first.lon, first.lat]);
    history.points.slice(1).forEach((point) => bounds.extend([point.lon, point.lat]));
    map.fitBounds(bounds, {
      padding: { top: 180, bottom: 135, left: 55, right: 55 },
      maxZoom: 20,
      duration: 650
    });
  }, [movementHistory]);

  const activateMovement = useCallback(async (mode: Exclude<MovementMode, null>) => {
    movementModeRef.current = mode;
    setMovementMode(mode);
    setMovementBusy(true);
    try {
      const data = await fetchJson<PositionHistoryResponse>(`/api/positions/history?range=${movementRange}`);
      setMovementHistory(data);
      const count = data.points.length;
      setMovementMessage(
        count > 0
          ? `${mode === "trail" ? "Route" : "Heatmap"}: ${count} Positionen in ${movementRangeLabel(data.range)}`
          : `Keine Positionen in den letzten ${movementRangeLabel(data.range)} gespeichert`
      );
      window.setTimeout(() => focusMovement(data), 0);
    } catch (error) {
      setMovementMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setMovementBusy(false);
    }
  }, [focusMovement, movementRange]);

  const hideMovement = useCallback(() => {
    movementModeRef.current = null;
    setMovementMode(null);
    setMovementMessage("Bewegungs-Anzeige ausgeblendet.");
  }, []);

  const beginDrawingMode = useCallback((mode: DrawingMode) => {
    setDrawingMode(mode);
    setDrawingDraft([]);
    setDrawingHover(null);
    const labels: Record<DrawingMode, string> = {
      select: "Karte bewegen",
      line: "Linie: zwei Punkte auf der Karte antippen.",
      rectangle: "Rechteck: eine Ecke antippen, dann die gegenüberliegende.",
      polygon: "Polygon: Eckpunkte antippen, dann „Fertig“ (oder Doppeltipp).",
      text: "Text: Karte antippen und Beschriftung eingeben.",
      eyedropper: "Pipette: Karte antippen, um eine Farbe aufzunehmen.",
      erase: "Radierer: Notiz antippen, um sie zu entfernen."
    };
    setDrawingMessage(labels[mode]);
    if (mode !== "select") {
      setDrawerOpen(false);
    }
  }, []);

  const addDrawing = useCallback((drawing: DrawingFeature) => {
    setDrawings((items) => [...items, drawing]);
    fetchJson<DrawingFeature>("/api/annotations", { method: "POST", body: JSON.stringify(drawing) }).catch((error) =>
      setDrawingMessage(error instanceof Error ? error.message : String(error))
    );
  }, []);

  const removeDrawing = useCallback((id: string) => {
    setDrawings((items) => items.filter((item) => item.id !== id));
    fetchJson(`/api/annotations/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(() => undefined);
  }, []);

  const finishPolygon = useCallback(() => {
    const draft = drawingDraftRef.current;
    if (draft.length < 3) {
      setDrawingMessage(alertAreaDrawingRef.current ? "Ein Bereich braucht mindestens drei Punkte." : "Ein Polygon braucht mindestens drei Punkte.");
      return;
    }
    if (alertAreaDrawingRef.current) {
      const polygon = [...draft];
      const meta = pendingAlertMetaRef.current;
      pendingAlertMetaRef.current = null;
      setAlertAreaDrawing(false);
      alertAreaDrawingRef.current = false;
      setDrawingDraft([]);
      setDrawingHover(null);
      setDrawingMode("select");
      setDrawingMessage("");
      setAlertAreaForm({
        editingId: meta?.editingId ?? null,
        polygon,
        name: meta?.name ?? "",
        direction: meta?.direction ?? "inside",
        notifyMode: meta?.notifyMode ?? "once_on_request",
        buttonText: meta?.buttonText ?? "",
        color: meta?.color ?? "#2c5cbe"
      });
      setDrawerOpen(true);
      return;
    }
    addDrawing({
      id: makeDrawingId(),
      type: "polygon",
      coordinates: ensureClosedPolygon(draft),
      color: drawingColorRef.current,
      createdAt: new Date().toISOString()
    });
    setDrawingDraft([]);
    setDrawingHover(null);
    setDrawingMode("select");
    setDrawingMessage("Polygon hinzugefügt.");
  }, [addDrawing]);

  const beginAlertAreaDraw = useCallback((meta?: NonNullable<typeof pendingAlertMetaRef.current>) => {
    pendingAlertMetaRef.current = meta ?? null;
    setAlertAreaForm(null);
    setAlertAreaDrawing(true);
    alertAreaDrawingRef.current = true;
    setDrawingMode("polygon");
    setDrawingDraft([]);
    setDrawingHover(null);
    setDrawingMessage(
      meta?.editingId
        ? "Neues Polygon zeichnen: Eckpunkte tippen, dann „Bereich abschließen“ (oder Doppeltipp)."
        : "Tippe die Eckpunkte des Bereichs, dann „Bereich abschließen“ (oder Doppeltipp)."
    );
    setDrawerOpen(false);
  }, []);

  const saveAlertAreaForm = useCallback(async () => {
    const form = alertAreaForm;
    if (!form) return;
    if (form.polygon.length < 3) {
      setAlertAreaMessage("Bereich braucht mindestens drei Punkte.");
      return;
    }
    if (!form.name.trim()) {
      setAlertAreaMessage("Bitte einen Namen vergeben.");
      return;
    }
    const body = {
      name: form.name.trim(),
      polygon: form.polygon,
      direction: form.direction,
      notifyMode: form.notifyMode,
      buttonText: form.notifyMode === "once_on_request" ? form.buttonText.trim() || null : null,
      color: form.color,
      enabled: true
    };
    try {
      if (form.editingId) {
        await fetchJson<AlertArea>(`/api/alert-areas/${form.editingId}`, { method: "PUT", body: JSON.stringify(body) });
      } else {
        await fetchJson<AlertArea>("/api/alert-areas", { method: "POST", body: JSON.stringify(body) });
      }
      setAlertAreaForm(null);
      setAlertAreaMessage(form.editingId ? "Bereich aktualisiert." : "Bereich angelegt.");
      await loadState();
    } catch (error) {
      setAlertAreaMessage(error instanceof Error ? error.message : String(error));
    }
  }, [alertAreaForm, loadState]);

  const closeAlertAreaForm = useCallback(() => {
    setAlertAreaForm(null);
  }, []);

  const deleteAlertArea = useCallback(
    async (area: AlertArea) => {
      if (!window.confirm(`Bereich „${area.name}“ löschen?`)) return;
      try {
        await fetchJson(`/api/alert-areas/${area.id}`, { method: "DELETE" });
        setAlertAreaMessage("Bereich gelöscht.");
        await loadState();
      } catch (error) {
        setAlertAreaMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [loadState]
  );

  const toggleAlertAreaEnabled = useCallback(
    async (area: AlertArea) => {
      try {
        await fetchJson<AlertArea>(`/api/alert-areas/${area.id}`, {
          method: "PUT",
          body: JSON.stringify({
            name: area.name,
            polygon: area.polygon,
            direction: area.direction,
            notifyMode: area.notifyMode,
            buttonText: area.buttonText,
            color: area.color,
            enabled: !area.enabled
          })
        });
        await loadState();
      } catch (error) {
        setAlertAreaMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [loadState]
  );

  const clearDrawings = useCallback(() => {
    if (!window.confirm("Alle Karten-Notizen löschen?")) return;
    setDrawings([]);
    setDrawingDraft([]);
    setDrawingHover(null);
    setDrawingMode("select");
    setDrawingMessage("Notizen gelöscht.");
    fetchJson("/api/annotations/clear", { method: "POST" }).catch(() => undefined);
  }, []);

  const ensurePushSubscription = useCallback(async (): Promise<boolean> => {
    const vapid = config?.vapidPublicKey;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !window.isSecureContext) {
      setNotificationMessage("Push wird auf diesem Gerät/Browser nicht unterstützt.");
      return false;
    }
    if (!vapid) {
      setNotificationMessage("Auf dem Server sind keine Push-Schlüssel konfiguriert.");
      return false;
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid) as unknown as BufferSource
        });
      }
      const json = subscription.toJSON();
      pushEndpointRef.current = subscription.endpoint;
      await fetchJson("/api/push/subscribe", {
        method: "POST",
        body: JSON.stringify({ endpoint: subscription.endpoint, keys: json.keys, userAgent: navigator.userAgent })
      });
      return true;
    } catch (error) {
      setNotificationMessage(error instanceof Error ? error.message : String(error));
      return false;
    }
  }, [config]);

  const enableNotifications = useCallback(async (): Promise<boolean> => {
    if (typeof Notification === "undefined") {
      setNotificationMessage("Dieser Browser unterstützt keine Benachrichtigungen.");
      return false;
    }
    let permission = Notification.permission;
    if (permission === "default") permission = await Notification.requestPermission();
    if (permission !== "granted") {
      setNotificationsEnabled(false);
      setNotificationMessage("Benachrichtigungen wurden nicht erlaubt. Bitte in den Browser-Einstellungen freigeben.");
      return false;
    }
    const ok = await ensurePushSubscription();
    setNotificationsEnabled(ok);
    if (ok) setNotificationMessage("Benachrichtigungen aktiviert auf diesem Gerät.");
    return ok;
  }, [ensurePushSubscription]);

  const sendTestNotification = useCallback(async () => {
    try {
      const result = await fetchJson<{ sent: number }>("/api/push/test", { method: "POST" });
      setNotificationMessage(`Test an ${result.sent} Gerät(e) gesendet.`);
    } catch (error) {
      setNotificationMessage(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const armAlertArea = useCallback(
    async (area: AlertArea) => {
      const granted = typeof Notification !== "undefined" && Notification.permission === "granted";
      if (!granted) {
        const ok = await enableNotifications();
        if (!ok) {
          setAlertAreaMessage("Bitte zuerst Benachrichtigungen erlauben.");
          return;
        }
      } else {
        await ensurePushSubscription();
      }
      const endpoint = pushEndpointRef.current;
      if (!endpoint) {
        setAlertAreaMessage("Kein Push-Abo auf diesem Gerät – bitte Benachrichtigungen aktivieren.");
        return;
      }
      try {
        await fetchJson(`/api/alert-areas/${area.id}/arm`, { method: "POST", body: JSON.stringify({ endpoint }) });
        setAlertAreaMessage(`„${area.name}“ auf diesem Gerät scharf geschaltet – nur du wirst benachrichtigt.`);
        await loadState();
      } catch (error) {
        setAlertAreaMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [enableNotifications, ensurePushSubscription, loadState]
  );

  const disarmAlertArea = useCallback(
    async (area: AlertArea) => {
      const endpoint = pushEndpointRef.current;
      if (!endpoint) {
        await loadState();
        return;
      }
      try {
        await fetchJson(`/api/alert-areas/${area.id}/disarm`, { method: "POST", body: JSON.stringify({ endpoint }) });
        await loadState();
      } catch (error) {
        setAlertAreaMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [loadState]
  );

  const editAlertAreaMeta = useCallback((area: AlertArea) => {
    setAlertAreaForm({
      editingId: area.id,
      polygon: area.polygon,
      name: area.name,
      direction: area.direction,
      notifyMode: area.notifyMode,
      buttonText: area.buttonText || "",
      color: area.color
    });
    setDrawerOpen(true);
  }, []);

  const toggleSettingsSection = useCallback((section: SettingsSectionKey) => {
    setOpenSettingsSections((current) => ({ ...current, [section]: !current[section] }));
  }, []);

  // After a deploy resets server-side push subscriptions (ephemeral SQLite),
  // silently re-subscribe on load if the user already granted permission.
  useEffect(() => {
    if (!config || pushSyncedRef.current) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted" && config.vapidPublicKey) {
      pushSyncedRef.current = true;
      ensurePushSubscription().then((ok) => setNotificationsEnabled(ok));
    }
  }, [config, ensurePushSubscription]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const sampleMapColor = (point: { x: number; y: number }) => {
      const canvas = map.getCanvas();
      const gl = (canvas.getContext("webgl2") || canvas.getContext("webgl")) as WebGLRenderingContext | WebGL2RenderingContext | null;
      if (!gl) return null;
      const scaleX = canvas.width / canvas.clientWidth;
      const scaleY = canvas.height / canvas.clientHeight;
      const pixel = new Uint8Array(4);
      try {
        gl.readPixels(
          Math.max(0, Math.min(canvas.width - 1, Math.round(point.x * scaleX))),
          Math.max(0, Math.min(canvas.height - 1, Math.round(canvas.height - point.y * scaleY))),
          1,
          1,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          pixel
        );
      } catch {
        return null;
      }
      if (pixel[3] === 0) return null;
      return `#${[pixel[0], pixel[1], pixel[2]].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
    };

    const eraseAtPoint = (point: { x: number; y: number }) => {
      const layers = [DRAWING_TEXT_LAYER_ID, DRAWING_LINE_LAYER_ID, DRAWING_FILL_LAYER_ID].filter((id) => map.getLayer(id));
      const features = map.queryRenderedFeatures([point.x, point.y], { layers });
      const id = features[0]?.properties?.id as string | undefined;
      if (!id) {
        setDrawingMessage("Keine Notiz an dieser Stelle.");
        return;
      }
      removeDrawing(id);
      setDrawingMessage("Notiz entfernt.");
    };

    let lastTapAt = 0;
    const handleDrawingTap = (
      lngLat: { lng: number; lat: number },
      screenPoint: { x: number; y: number },
      originalEvent?: Pick<Event, "preventDefault" | "stopPropagation">
    ) => {
      const mode = drawingMode;
      if (mode === "select" || receiverEditMode) return false;
      originalEvent?.preventDefault();
      originalEvent?.stopPropagation();
      const point: [number, number] = [lngLat.lng, lngLat.lat];

      if (mode === "eyedropper") {
        const sampled = sampleMapColor(screenPoint);
        if (sampled) {
          setDrawingColor(sampled);
          setDrawingMessage(`Farbe ${sampled} aufgenommen.`);
        } else {
          setDrawingMessage("Hier konnte keine Farbe aufgenommen werden.");
        }
        setDrawingMode("select");
        return true;
      }

      if (mode === "erase") {
        eraseAtPoint(screenPoint);
        return true;
      }

      if (mode === "text") {
        const text = window.prompt("Text für die Notiz");
        if (!text?.trim()) {
          setDrawingMessage("Text-Notiz abgebrochen.");
          return true;
        }
        addDrawing({
          id: makeDrawingId(),
          type: "text",
          coordinates: [point],
          color: drawingColorRef.current,
          text: text.trim(),
          createdAt: new Date().toISOString()
        });
        setDrawingMessage("Text-Notiz hinzugefügt.");
        return true;
      }

      const draft = drawingDraftRef.current;
      if (mode === "line") {
        if (draft.length === 0) {
          setDrawingDraft([point]);
          setDrawingMessage("Linie begonnen. Endpunkt antippen.");
          return true;
        }
        addDrawing({
          id: makeDrawingId(),
          type: "line",
          coordinates: [draft[0], point],
          color: drawingColorRef.current,
          createdAt: new Date().toISOString()
        });
        setDrawingDraft([]);
        setDrawingHover(null);
        setDrawingMode("select");
        setDrawingMessage("Linie hinzugefügt.");
        return true;
      }

      if (mode === "rectangle") {
        if (draft.length === 0) {
          setDrawingDraft([point]);
          setDrawingMessage("Rechteck begonnen. Gegenüberliegende Ecke antippen.");
          return true;
        }
        addDrawing({
          id: makeDrawingId(),
          type: "rectangle",
          coordinates: rectangleCoordinates(draft[0], point),
          color: drawingColorRef.current,
          createdAt: new Date().toISOString()
        });
        setDrawingDraft([]);
        setDrawingHover(null);
        setDrawingMode("select");
        setDrawingMessage("Rechteck hinzugefügt.");
        return true;
      }

      if (mode === "polygon") {
        setDrawingDraft([...draft, point]);
        setDrawingMessage(`${draft.length + 1} Polygon-Punkte. Zum Abschließen „Fertig“ tippen.`);
        return true;
      }
      return false;
    };

    const onClick = (event: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTapAt < 140) return;
      if (handleDrawingTap(event.lngLat, event.point, event.originalEvent)) {
        lastTapAt = Date.now();
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      const mode = drawingMode;
      if (mode === "select" || receiverEditMode) return;
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      if (Date.now() - lastTapAt < 140) return;
      const lngLat = map.unproject([x, y]);
      if (handleDrawingTap(lngLat, { x, y }, event)) {
        lastTapAt = Date.now();
      }
    };

    const onMove = (event: mapboxgl.MapMouseEvent) => {
      const mode = drawingMode;
      if (!["line", "rectangle", "polygon"].includes(mode)) return;
      if (drawingDraftRef.current.length === 0) return;
      setDrawingHover([event.lngLat.lng, event.lngLat.lat]);
    };

    const onDoubleClick = (event: mapboxgl.MapMouseEvent) => {
      if (drawingMode !== "polygon") return;
      event.originalEvent.preventDefault();
      finishPolygon();
    };

    const canvasContainer = map.getCanvasContainer();
    canvasContainer.addEventListener("pointerup", onPointerUp, { capture: true });
    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDoubleClick);
    return () => {
      canvasContainer.removeEventListener("pointerup", onPointerUp, { capture: true });
      map.off("click", onClick);
      map.off("mousemove", onMove);
      map.off("dblclick", onDoubleClick);
    };
  }, [addDrawing, removeDrawing, drawingMode, finishPolygon, receiverEditMode]);

  const toggleTrainingWalk = useCallback(async () => {
    if (trainingWalkVisible) {
      setTrainingWalkVisible(false);
      setTrainingWalkMessage(trainingWalk ? `${trainingWalkLabel(trainingWalk)} ausgeblendet` : "Walk ausgeblendet");
      return;
    }
    setTrainingWalkBusy(true);
    try {
      const walk = await fetchJson<TrainingWalk>("/api/training/walk/latest");
      if (!walk.session || walk.samples.length === 0) {
        setTrainingWalk(null);
        setTrainingWalkVisible(false);
        setTrainingWalkMessage("Noch kein Trainings-Walk gespeichert");
        return;
      }
      setTrainingWalk(walk);
      setTrainingWalkVisible(true);
      setTrainingWalkMessage(trainingWalkLabel(walk));
      window.setTimeout(() => focusTrainingWalk(walk), 0);
    } catch (error) {
      setTrainingWalkMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setTrainingWalkBusy(false);
    }
  }, [focusTrainingWalk, trainingWalk, trainingWalkVisible]);

  const captureTrainingSample = useCallback(async (sessionOverride?: string) => {
    const activeSessionId = sessionOverride || trainingSessionIdRef.current;
    const location = userLocationRef.current;
    if (!activeSessionId || !location) return;
    const response = await fetchJson<{ sampleCount: number }>("/api/training/sample", {
      method: "POST",
      body: JSON.stringify({
        sessionId: activeSessionId,
        lat: location.lat,
        lon: location.lon,
        gpsAccuracyM: location.accuracy,
        labelSource: "phone_gps"
      })
    });
    setTrainingMessage(`${response.sampleCount} Trainingspunkte gespeichert`);
    await loadState();
  }, [loadState]);

  useEffect(() => {
    if (!trainingSessionId) return;
    const interval = window.setInterval(() => {
      captureTrainingSample().catch((error) => setTrainingMessage(error instanceof Error ? error.message : String(error)));
    }, TRAINING_SAMPLE_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [captureTrainingSample, trainingSessionId]);

  async function startTraining(mode: "single" | "cumulative" = "single") {
    if (!userLocation) {
      setTrainingMessage("Warte auf GPS-Freigabe.");
      return;
    }
    setTrainingBusy(true);
    try {
      const response = await fetchJson<{ sessionId: string }>("/api/training/start", {
        method: "POST",
        body: JSON.stringify({ notes: mode === "cumulative" ? "Extend and merge" : "Train from new data only" })
      });
      setTrainingSessionId(response.sessionId);
      setTrainingMode(mode);
      setTrainingMessage(mode === "cumulative" ? "Erweitern läuft. Neue und vorhandene Punkte werden beim Stoppen zusammengeführt." : "Training läuft.");
      await captureTrainingSample(response.sessionId);
    } catch (error) {
      setTrainingMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setTrainingBusy(false);
    }
  }

  async function stopTraining() {
    const sessionId = trainingSessionId;
    if (!sessionId) return;
    const cumulative = trainingMode === "cumulative";
    setTrainingBusy(true);
    trainingSessionIdRef.current = null;
    setTrainingSessionId(null);
    try {
      await captureTrainingSample(sessionId);
      const response = await fetchJson<{ model: ModelInfo }>("/api/training/finish", {
        method: "POST",
        body: JSON.stringify({ sessionId, cumulative })
      });
      setTrainingMessage(response.model ? `Modell aktiv: ${response.model.engine}` : "Training abgeschlossen");
      setTrainingMode("single");
      await Promise.all([loadState(), loadTrainingSummary()]);
    } catch (error) {
      setTrainingMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setTrainingBusy(false);
    }
  }

  async function requestInstall() {
    if (standaloneInstalled || isStandaloneApp()) {
      setStandaloneInstalled(true);
      setInstallMessage("Bereits auf diesem Gerät installiert.");
      setInstallToastVisible(false);
      return;
    }
    if (!deferredInstallPrompt) {
      setInstallMessage(
        isIosSafari()
          ? "In Safari auf das Teilen-Symbol tippen, dann „Zum Home-Bildschirm“."
          : "Über das Browser-Menü installieren oder zum Startbildschirm hinzufügen."
      );
      return;
    }
    await deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setInstallMessage("Installation gestartet.");
      setInstallToastVisible(false);
    } else {
      setInstallMessage("Installation abgelehnt.");
      snoozeInstallPrompt();
      setInstallToastVisible(false);
    }
    setDeferredInstallPrompt(null);
  }

  function dismissInstallToast() {
    snoozeInstallPrompt();
    setInstallToastVisible(false);
    setIosInstallHint(false);
  }

  const estimate = appState?.estimate;
  const isLive = estimate?.isLive ?? false;
  // System status for the top panel: receiver fleet health + how many boards
  // currently contribute to the estimate (key to judging its quality when
  // individual boards drop out).
  const activeReceiverCount = (appState?.receivers || []).filter((receiver) => receiver.isActive).length;
  const onlineReceiverCount = (appState?.receivers || []).filter((receiver) => {
    if (!receiver.isActive) return false;
    return isReceiverOnline(findReceiverTelemetry(appState, receiver));
  }).length;
  const nodesSeen = typeof estimate?.details?.nodesSeenFeatureWindow === "number"
    ? (estimate.details.nodesSeenFeatureWindow as number)
    : null;
  const alertAreas = appState?.alertAreas || [];
  // On-map arm buttons: only for one-shot areas, and only when arming makes
  // sense — i.e. the condition is not already met (no point offering "tell me
  // when Siegfried is in the garden" while he is already there). Armed areas
  // always stay visible so the user can cancel.
  const armableAreas = useMemo(
    () =>
      alertAreas.filter(
        (area) =>
          area.enabled &&
          area.notifyMode === "once_on_request" &&
          (area.armedForDevice || area.conditionMet !== true)
      ),
    [alertAreas]
  );
  const tokenMissing = config && !config.mapboxToken;
  const estimateStatusText = estimate
    ? `Siegfried zuletzt gesehen vor ${formatAgo(estimate.estimatedAt)}`
    : "Siegfried wurde noch nicht gesehen";
  const boardsDegraded = activeReceiverCount > 0 && onlineReceiverCount < Math.min(3, activeReceiverCount);

  return (
    <main className={`shell ${receiverEditMode ? "editing-receivers" : ""}`}>
      <div className="map" ref={mapContainerRef} />
      {tokenMissing ? <div className="map-error">Mapbox-Token fehlt.</div> : null}
      <InstallToast visible={installToastVisible} ios={iosInstallHint} onInstall={requestInstall} onDismiss={dismissInstallToast} />

      <section className="top-panel">
        <div>
          <div className="eyebrow"><Cat size={15} /> Siegfried</div>
          <h1>{isLive ? "Aktuelle Position" : "Letzte bekannte Position"}</h1>
          {!isLive ? <p className="status stale">{estimateStatusText}</p> : null}
          <div className="badge-row">
            <FreshnessBadge lastUpdatedAt={lastStateFetchedAt} hasError={Boolean(lastError)} />
            {activeReceiverCount > 0 ? (
              <span
                className={`freshness-badge ${boardsDegraded ? "stale" : "fresh"}`}
                title="Empfänger-Boards online / gesamt · Boards mit aktuellem Signal vom Halsband"
              >
                <Radio size={13} />
                {onlineReceiverCount}/{activeReceiverCount} Boards
                {isLive && nodesSeen != null ? ` · Signal von ${nodesSeen}` : ""}
              </span>
            ) : null}
          </div>
        </div>
        <button className="icon-button" aria-label="Einstellungen" onClick={() => setDrawerOpen(true)}>
          <Settings size={21} />
        </button>
      </section>

      <section className="direction-panel">
        <div className="compass">
          <Navigation size={35} style={{ transform: `rotate(${navigation?.relative ?? 0}deg)` }} />
        </div>
        <div>
          <span>Richtung</span>
          <strong>{navigation ? `${Math.round(navigation.bearing)}°` : "-"}</strong>
        </div>
        <div>
          <span>Entfernung</span>
          <strong>{formatDistance(navigation?.distance)}</strong>
        </div>
        <div>
          <span>Genauigkeit</span>
          <strong>{formatDistance(estimate?.accuracyM)}</strong>
        </div>
      </section>

      {receiverEditMode ? (
        <section className="receiver-edit-panel">
          <div>
            <div className="eyebrow"><MapPin size={15} /> Empfänger</div>
            <strong>Empfänger positionieren</strong>
            <p>{receiverSaveMessage || "Aktive Empfänger-Marker an ihre echten Positionen ziehen"}</p>
          </div>
          <button className="secondary-action" onClick={() => setReceiverEditMode(false)}>
            <X size={16} /> Fertig
          </button>
        </section>
      ) : null}

      {drawingMode !== "select" ? (
        <section className="drawing-panel">
          <div>
            <div className="eyebrow">{alertAreaDrawing ? <ShieldAlert size={15} /> : <PenLine size={15} />} {alertAreaDrawing ? "Alarm-Bereich" : "Zeichnen"}</div>
            <strong>{drawingMessage || "Karte beschriften"}</strong>
          </div>
          {drawingMode === "polygon" && drawingDraft.length >= 3 ? (
            <button className="primary-action" onClick={finishPolygon}>
              <Pentagon size={16} /> {alertAreaDrawing ? "Bereich abschließen" : "Fertig"}
            </button>
          ) : null}
          <button
            className="secondary-action"
            onClick={() => {
              setAlertAreaDrawing(false);
              alertAreaDrawingRef.current = false;
              pendingAlertMetaRef.current = null;
              beginDrawingMode("select");
            }}
          >
            <X size={16} /> Stop
          </button>
        </section>
      ) : null}

      {armableAreas.length > 0 ? (
        <section className="alert-arm-panel">
          {armableAreas.map((area) => (
            <div key={area.id} className={`alert-arm-pill ${area.armedForDevice ? "armed" : ""}`}>
              {area.armedForDevice ? (
                <>
                  <BellRing size={16} />
                  <span>{area.buttonText || area.name} · scharf</span>
                  <button className="link-button" onClick={() => disarmAlertArea(area)}>
                    Abbrechen
                  </button>
                </>
              ) : (
                <button className="alert-arm-action" onClick={() => armAlertArea(area)}>
                  <Bell size={16} /> {area.buttonText || `Benachrichtigen: ${area.name}`}
                </button>
              )}
            </div>
          ))}
        </section>
      ) : null}

      {lastError ? (
        <div className="error-toast">
          <RefreshCw size={15} />
          {lastError}
        </div>
      ) : null}

      <aside className={`drawer ${drawerOpen ? "open" : ""}`} aria-hidden={!drawerOpen}>
        <header>
          <div>
            <span className="eyebrow">Einstellungen</span>
            <h2>System</h2>
            <FreshnessBadge lastUpdatedAt={lastStateFetchedAt} hasError={Boolean(lastError)} />
          </div>
          <button className="icon-button ghost" aria-label="Schließen" onClick={() => setDrawerOpen(false)}>
            <X size={21} />
          </button>
        </header>

        <CollapsibleSection
          title="Siegfrieds Bewegung"
          icon={<Layers size={16} />}
          open={openSettingsSections.movement}
          onToggle={() => toggleSettingsSection("movement")}
          className="priority-section"
        >
          <div className="segmented-control movement-range" role="radiogroup" aria-label="Zeitraum der Bewegung">
            {(["10m", "60m", "6h", "24h"] as MovementRange[]).map((range) => (
              <button
                key={range}
                type="button"
                className={movementRange === range ? "active" : ""}
                onClick={() => setMovementRange(range)}
                disabled={!movementMode || movementBusy}
                aria-pressed={movementRange === range}
                title={movementMode ? `Zeige die letzten ${movementRangeLabel(range)}` : "Zuerst Route oder Heatmap wählen"}
              >
                {movementRangeLabel(range)}
              </button>
            ))}
          </div>
          <div className="segmented-control movement-mode" role="radiogroup" aria-label="Darstellung der Bewegung">
            <button
              type="button"
              className={movementMode === "trail" ? "active" : ""}
              onClick={() => activateMovement("trail")}
              disabled={movementBusy}
              aria-pressed={movementMode === "trail"}
            >
              <Activity size={16} /> Route
            </button>
            <button
              type="button"
              className={movementMode === "heatmap" ? "active" : ""}
              onClick={() => activateMovement("heatmap")}
              disabled={movementBusy}
              aria-pressed={movementMode === "heatmap"}
            >
              <Layers size={16} /> Heatmap
            </button>
          </div>
          <div className="training-actions compact-actions">
            <button className="secondary-action" onClick={() => focusMovement()} disabled={!movementHistory || movementHistory.points.length === 0}>
              <Navigation size={16} /> Zoom
            </button>
            <button className="secondary-action" onClick={hideMovement} disabled={!movementMode}>
              <EyeOff size={16} /> Ausblenden
            </button>
          </div>
          <p className="small">{movementMessage || "Zeigt Siegfrieds letzte Bewegung als geglättete Route oder als Revier-Heatmap."}</p>
        </CollapsibleSection>

        <CollapsibleSection
          title="Alarm-Bereiche"
          icon={<ShieldAlert size={16} />}
          open={openSettingsSections.alerts}
          onToggle={() => toggleSettingsSection("alerts")}
          className="priority-section"
        >
          <p className="small">
            Zeichne ein Polygon (z. B. euren Garten) und lass dich benachrichtigen, wenn Siegfried hinein- oder
            herausläuft.
          </p>
          <div className="training-actions compact-actions">
            <button className="primary-action" onClick={() => beginAlertAreaDraw()}>
              <Plus size={16} /> Bereich zeichnen
            </button>
          </div>
          {alertAreas.length === 0 ? (
            <p className="small">Noch keine Bereiche angelegt.</p>
          ) : (
            <div className="alert-area-list">
              {alertAreas.map((area) => (
                <div className={`alert-area-row ${area.enabled ? "" : "disabled"}`} key={area.id}>
                  <span className="alert-area-info">
                    <strong style={{ color: area.color }}>{area.name}</strong>
                    <small>
                      {area.direction === "inside" ? "innerhalb" : "außerhalb"} ·{" "}
                      {area.notifyMode === "always" ? "immer" : "auf Anfrage"}
                      {area.currentlyInside != null ? (area.currentlyInside ? " · Siegfried ist drin" : " · Siegfried ist draußen") : ""}
                    </small>
                    {area.notifyMode === "once_on_request" && area.buttonText ? (
                      <small className="alert-area-button-text">„{area.buttonText}“</small>
                    ) : null}
                  </span>
                  <span className="alert-area-actions">
                    <label className="alert-area-toggle" title="Aktiv">
                      <input type="checkbox" checked={area.enabled} onChange={() => toggleAlertAreaEnabled(area)} />
                      <span>{area.enabled ? "an" : "aus"}</span>
                    </label>
                    <button className="icon-button ghost" aria-label="Bearbeiten" onClick={() => editAlertAreaMeta(area)}>
                      <Pencil size={16} />
                    </button>
                    <button className="icon-button ghost" aria-label="Löschen" onClick={() => deleteAlertArea(area)}>
                      <Trash2 size={16} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
          {alertAreaMessage ? <p className="small">{alertAreaMessage}</p> : null}
        </CollapsibleSection>

        <CollapsibleSection
          title="Benachrichtigungen"
          icon={<Bell size={16} />}
          open={openSettingsSections.notifications}
          onToggle={() => toggleSettingsSection("notifications")}
        >
          <div className="training-actions compact-actions">
            <button className="primary-action" onClick={enableNotifications} disabled={notificationsEnabled}>
              <Bell size={16} /> {notificationsEnabled ? "Aktiviert" : "Benachrichtigungen aktivieren"}
            </button>
            <button className="secondary-action" onClick={sendTestNotification} disabled={!notificationsEnabled}>
              <BellRing size={16} /> Test
            </button>
          </div>
          <p className="small">
            {notificationMessage ||
              (notificationsEnabled
                ? "Push-Benachrichtigungen sind auf diesem Gerät aktiv – auch wenn die App geschlossen ist (PWA)."
                : "Aktiviere Web-Push, um Alarme dieses Geräts zu empfangen.")}
          </p>
        </CollapsibleSection>

        <CollapsibleSection
          title="Karten-Notizen"
          icon={<PenLine size={16} />}
          open={openSettingsSections.annotations}
          onToggle={() => toggleSettingsSection("annotations")}
        >
          <div className="segmented-control drawing-tool-group" role="radiogroup" aria-label="Zeichenwerkzeug">
            <button type="button" className={drawingMode === "select" ? "active" : ""} onClick={() => beginDrawingMode("select")} aria-pressed={drawingMode === "select"} title="Karte bewegen und ansehen">
              <MousePointer2 size={16} /> Bewegen
            </button>
            <button type="button" className={drawingMode === "line" ? "active" : ""} onClick={() => beginDrawingMode("line")} aria-pressed={drawingMode === "line"} title="Linie zeichnen">
              <PenLine size={16} /> Linie
            </button>
            <button type="button" className={drawingMode === "rectangle" ? "active" : ""} onClick={() => beginDrawingMode("rectangle")} aria-pressed={drawingMode === "rectangle"} title="Rechteck zeichnen">
              <RectangleHorizontal size={16} /> Rechteck
            </button>
            <button type="button" className={drawingMode === "polygon" ? "active" : ""} onClick={() => beginDrawingMode("polygon")} aria-pressed={drawingMode === "polygon"} title="Polygon zeichnen">
              <Pentagon size={16} /> Polygon
            </button>
            <button type="button" className={drawingMode === "text" ? "active" : ""} onClick={() => beginDrawingMode("text")} aria-pressed={drawingMode === "text"} title="Text platzieren">
              <TypeIcon size={16} /> Text
            </button>
            <button type="button" className={drawingMode === "eyedropper" ? "active" : ""} onClick={() => beginDrawingMode("eyedropper")} aria-pressed={drawingMode === "eyedropper"} title="Farbe von der Karte aufnehmen">
              <Droplet size={16} /> Pipette
            </button>
            <button type="button" className={drawingMode === "erase" ? "active" : ""} onClick={() => beginDrawingMode("erase")} aria-pressed={drawingMode === "erase"} title="Notiz entfernen">
              <Eraser size={16} /> Radierer
            </button>
          </div>
          <div className="color-control">
            <Palette size={16} />
            <input aria-label="Zeichenfarbe" type="color" value={drawingColor} onChange={(event) => setDrawingColor(event.target.value)} />
            <span style={{ background: drawingColor }} />
            <strong>{drawingColor}</strong>
          </div>
          <div className="training-actions compact-actions">
            {drawingDraft.length > 0 ? (
              <button className="secondary-action" onClick={finishPolygon} disabled={drawingDraft.length < 3}>
                <Pentagon size={16} /> Polygon abschließen
              </button>
            ) : null}
            <button className="danger-action" onClick={clearDrawings} disabled={drawings.length === 0}>
              <Trash2 size={16} /> Alle löschen
            </button>
          </div>
          <p className="small">{drawingMessage || `${drawings.length} ${drawings.length === 1 ? "Notiz" : "Notizen"} gespeichert – auf allen Geräten sichtbar.`}</p>
        </CollapsibleSection>

        <CollapsibleSection
          title="App-Installation"
          icon={<Smartphone size={16} />}
          open={openSettingsSections.install}
          onToggle={() => toggleSettingsSection("install")}
        >
          <div className="training-actions compact-actions">
            <button className="primary-action" onClick={requestInstall} disabled={standaloneInstalled}>
              <Download size={16} /> {standaloneInstalled ? "Installiert" : "App installieren"}
            </button>
          </div>
          <p className="small">
            {installMessage || appUpdateMessage || "Installierte Apps prüfen bei jedem Start automatisch auf die neueste Version."}
          </p>
        </CollapsibleSection>

        <CollapsibleSection
          title="MQTT"
          icon={<Wifi size={16} />}
          open={openSettingsSections.mqtt}
          onToggle={() => toggleSettingsSection("mqtt")}
        >
          <dl>
            <div><dt>Status</dt><dd>{appState?.mqtt?.subscriber_connected ? "verbunden" : "wartet"}</dd></div>
            <div><dt>Topic</dt><dd>{config?.mqtt.topic || "-"}</dd></div>
            <div><dt>TCP</dt><dd>{formatMqttTcp(config)}</dd></div>
          </dl>
        </CollapsibleSection>

        <CollapsibleSection
          title="Modell"
          icon={<Database size={16} />}
          open={openSettingsSections.model}
          onToggle={() => toggleSettingsSection("model")}
        >
          <dl>
            <div><dt>Engine</dt><dd>{appState?.model?.engine || "Fallback"}</dd></div>
            <div><dt>Signal-Boards</dt><dd>{typeof appState?.estimate?.details?.nodesSeenFeatureWindow === "number" ? String(appState.estimate.details.nodesSeenFeatureWindow) : "-"}</dd></div>
            <div><dt>Samples</dt><dd>{appState?.model?.sampleCount ?? 0}</dd></div>
            <div><dt>MAE</dt><dd>{formatDistance(Number(appState?.model?.metrics?.validation_mae_m ?? appState?.model?.metrics?.training_mae_m ?? NaN))}</dd></div>
          </dl>
        </CollapsibleSection>

        <CollapsibleSection
          title="Training"
          icon={<Activity size={16} />}
          open={openSettingsSections.training}
          onToggle={() => toggleSettingsSection("training")}
        >
          {trainingSummary && trainingSummary.totalSamples > 0 && (
            <p className="small">
              Vorhandene Trainingsdaten: <strong>{trainingSummary.totalSamples}</strong> Punkte aus{" "}
              <strong>{trainingSummary.finishedSessions}</strong> Sessions
              {trainingSummary.hasActiveModel ? " · aktives Modell vorhanden" : ""}.
            </p>
          )}
          <p className="small">
            {trainingSessionId
              ? trainingMode === "cumulative"
                ? `Erweitern läuft: GPS+RSSI wird alle ${TRAINING_SAMPLE_INTERVAL_SECONDS} s gespeichert; beim Stoppen werden neue und vorhandene Punkte zusammengeführt.`
                : `Training läuft: GPS+RSSI wird alle ${TRAINING_SAMPLE_INTERVAL_SECONDS} s gespeichert; beim Stoppen wird nur mit den neuen Punkten trainiert und das aktive Modell ersetzt.`
              : "Bereit, sobald GPS aktiv ist. „Trainieren“ nutzt nur neu gesammelte Punkte, „Erweitern“ ergänzt die vorhandenen Trainingsdaten."}
          </p>
          <div className="training-actions">
            {!trainingSessionId ? (
              <>
                <button
                  className="primary-action"
                  onClick={() => startTraining("single")}
                  disabled={trainingBusy || !userLocation}
                  title="Neues Modell nur aus jetzt gesammelten Punkten trainieren"
                >
                  <Play size={16} /> Trainieren
                </button>
                {trainingSummary && trainingSummary.totalSamples > 0 && (
                  <button
                    className="secondary-action"
                    onClick={() => startTraining("cumulative")}
                    disabled={trainingBusy || !userLocation}
                    title="Neues Modell aus vorhandenen und neuen Punkten trainieren"
                  >
                    <Play size={16} /> Erweitern
                  </button>
                )}
              </>
            ) : (
              <button className="danger-action" onClick={stopTraining} disabled={trainingBusy}>
                <Square size={16} /> Stopp + Trainieren
              </button>
            )}
            <button className="secondary-action" onClick={() => captureTrainingSample()} disabled={!trainingSessionId || trainingBusy || !userLocation}>
              <MapPin size={16} /> Punkt
            </button>
          </div>
          <p className="small">{trainingMessage || (userLocation ? `GPS ${formatDistance(userLocation.accuracy)}` : "GPS ist noch nicht aktiv")}</p>
          <div className="training-actions">
            <button className="secondary-action" onClick={toggleTrainingWalk} disabled={trainingWalkBusy}>
              <Activity size={16} /> {trainingWalkVisible ? "Walk ausblenden" : "Walk anzeigen"}
            </button>
            <button className="secondary-action" onClick={() => focusTrainingWalk()} disabled={!trainingWalk || trainingWalk.samples.length === 0}>
              <Navigation size={16} /> Zoom
            </button>
          </div>
          <p className="small">{trainingWalkMessage || "Zeigt den letzten Trainings-Walk als Linie"}</p>
          {trainingSummary && trainingSummary.totalSamples > 0 && !trainingSessionId && (
            <div className="training-actions">
              <button
                className="danger-action"
                onClick={resetTrainingData}
                disabled={trainingBusy}
                title="Alle gespeicherten Punkte löschen und das aktive Modell deaktivieren"
              >
                <Trash2 size={16} /> Trainingsdaten zurücksetzen
              </button>
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Empfänger"
          icon={<MapPin size={16} />}
          open={openSettingsSections.receivers}
          onToggle={() => toggleSettingsSection("receivers")}
          className="receivers"
        >
          <button
            className="secondary-action receiver-position-action"
            onClick={() => {
              setReceiverSaveMessage("");
              setReceiverEditMode(true);
              setDrawerOpen(false);
            }}
          >
            <MapPin size={16} /> Positionen
          </button>
          {(appState?.receivers || [])
            .filter((receiver) => receiver.isActive)
            .slice()
            .sort((a, b) => receiverSortKey(a) - receiverSortKey(b))
            .map((receiver) => {
            const signal = appState?.signals.find((item) => item.receiverId === receiver.id || item.room === receiver.room);
            const telemetry = findReceiverTelemetry(appState, receiver);
            const online = isReceiverOnline(telemetry);
            const boardUrl = telemetry?.ip ? `http://${telemetry.ip}` : null;
            return (
              <div className="receiver-row" key={receiver.id}>
                <span className="receiver-info">
                  <strong className="receiver-code">{receiverShortLabel(receiver)}</strong>
                  <span>{receiver.name}</span>
                  <small>{receiver.lat?.toFixed(6) || "-"}, {receiver.lon?.toFixed(6) || "-"}</small>
                  {boardUrl ? (
                    <a href={boardUrl} target="_blank" rel="noreferrer">
                      Board-Konfiguration öffnen
                    </a>
                  ) : (
                    <small>Noch keine Board-IP</small>
                  )}
                </span>
                <span className="receiver-metrics">
                  <strong className={`receiver-status ${online ? "online" : "offline"}`}>
                    {online ? "online" : "offline"}
                  </strong>
                  <SignalMeter label="WiFi" kind="wifi" value={telemetry?.wifiRssi} />
                  <SignalMeter label="BLE" kind="ble" value={signal?.rssi} />
                  <small>{formatReceiverWifi(telemetry)}</small>
                </span>
              </div>
            );
          })}
        </CollapsibleSection>

        <CollapsibleSection
          title="Diagnose · Board-Verfügbarkeit"
          icon={<BarChart3 size={16} />}
          open={openSettingsSections.diagnostics}
          onToggle={() => toggleSettingsSection("diagnostics")}
        >
          <div className="training-actions">
            <button
              className={availabilityRange === "24h" ? "primary-action" : "secondary-action"}
              onClick={() => setAvailabilityRange("24h")}
              disabled={availabilityBusy}
            >
              24h
            </button>
            <button
              className={availabilityRange === "7d" ? "primary-action" : "secondary-action"}
              onClick={() => setAvailabilityRange("7d")}
              disabled={availabilityBusy}
            >
              7d
            </button>
            <button
              className="secondary-action"
              onClick={() => loadAvailability(availabilityRange)}
              disabled={availabilityBusy}
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <a
              className="secondary-action"
              href="/diagnostics"
              title="Open in a full-screen view"
            >
              <BarChart3 size={16} /> Full Screen
            </a>
          </div>
          {availabilityMessage && <p className="small">{availabilityMessage}</p>}
          {availability && <AvailabilityChart data={availability} />}
        </CollapsibleSection>
      </aside>

      {alertAreaForm ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <section className="modal-card alert-area-form">
            <header>
              <strong>{alertAreaForm.editingId ? "Bereich bearbeiten" : "Neuer Alarm-Bereich"}</strong>
              <button className="icon-button ghost" aria-label="Schließen" onClick={closeAlertAreaForm}>
                <X size={20} />
              </button>
            </header>
            <label className="field">
              <span>Name</span>
              <input
                type="text"
                value={alertAreaForm.name}
                placeholder="z. B. Unser Garten"
                onChange={(event) => setAlertAreaForm((form) => (form ? { ...form, name: event.target.value } : form))}
              />
            </label>
            <div className="field">
              <span>Benachrichtigen wenn Siegfried…</span>
              <div className="segmented-control">
                <button
                  type="button"
                  className={alertAreaForm.direction === "inside" ? "active" : ""}
                  onClick={() => setAlertAreaForm((form) => (form ? { ...form, direction: "inside" } : form))}
                >
                  innerhalb ist
                </button>
                <button
                  type="button"
                  className={alertAreaForm.direction === "outside" ? "active" : ""}
                  onClick={() => setAlertAreaForm((form) => (form ? { ...form, direction: "outside" } : form))}
                >
                  außerhalb ist
                </button>
              </div>
            </div>
            <label className="field">
              <span>Wann benachrichtigen?</span>
              <select
                value={alertAreaForm.notifyMode}
                onChange={(event) =>
                  setAlertAreaForm((form) => (form ? { ...form, notifyMode: event.target.value as AlertNotifyMode } : form))
                }
              >
                <option value="once_on_request">Nur einmal, nachdem ich es per Button anfordere</option>
                <option value="always">Jedes Mal, wenn das Ereignis eintritt (alle Geräte)</option>
              </select>
            </label>
            <p className="small">
              {alertAreaForm.notifyMode === "always"
                ? "Modus „Jedes Mal“ benachrichtigt bei jedem Ereignis ALLE Geräte – ohne Button. Für den Scharfschalten-Button bitte „Nur einmal“ wählen."
                : "Zeigt auf der Karte einen Button; nur das Gerät, das ihn drückt, wird einmalig benachrichtigt."}
            </p>
            {alertAreaForm.notifyMode === "once_on_request" ? (
              <label className="field">
                <span>Button-Text</span>
                <input
                  type="text"
                  value={alertAreaForm.buttonText}
                  placeholder="z. B. Benachrichtigen wenn Siegfried wieder im Garten auftaucht"
                  onChange={(event) =>
                    setAlertAreaForm((form) => (form ? { ...form, buttonText: event.target.value } : form))
                  }
                />
              </label>
            ) : null}
            <label className="field">
              <span>Farbe</span>
              <input
                type="color"
                value={alertAreaForm.color}
                onChange={(event) => setAlertAreaForm((form) => (form ? { ...form, color: event.target.value } : form))}
              />
            </label>
            <div className="field">
              <span>Polygon ({alertAreaForm.polygon.length} Eckpunkte)</span>
              <button
                className="secondary-action"
                onClick={() =>
                  beginAlertAreaDraw({
                    editingId: alertAreaForm.editingId,
                    name: alertAreaForm.name,
                    direction: alertAreaForm.direction,
                    notifyMode: alertAreaForm.notifyMode,
                    buttonText: alertAreaForm.buttonText,
                    color: alertAreaForm.color
                  })
                }
              >
                <Pentagon size={16} /> Polygon neu zeichnen
              </button>
            </div>
            <p className="small">{alertAreaMessage}</p>
            <div className="training-actions">
              <button className="primary-action" onClick={saveAlertAreaForm}>
                {alertAreaForm.editingId ? "Speichern" : "Bereich anlegen"}
              </button>
              <button className="secondary-action" onClick={closeAlertAreaForm}>
                Abbrechen
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}

function DiagnosticsApp() {
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [range, setRange] = useState<"24h" | "7d">("24h");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (selectedRange: "24h" | "7d") => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchJson<AvailabilityResponse>(`/api/diagnostics/availability?range=${selectedRange}`);
      setAvailability(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load(range);
    const interval = window.setInterval(() => load(range), 30000);
    return () => window.clearInterval(interval);
  }, [load, range]);

  return (
    <main className="walk-shell">
      <section className="walk-header">
        <div>
          <div className="eyebrow"><BarChart3 size={15} /> Diagnostics</div>
          <h1>Board Availability</h1>
          <p>Online/offline history for each receiver over the selected window.</p>
        </div>
        <button className="icon-button" aria-label="Refresh" onClick={() => load(range)} disabled={busy}>
          <RefreshCw size={20} />
        </button>
      </section>

      <section className="diagnostics-controls">
        <button
          className={range === "24h" ? "primary-action" : "secondary-action"}
          onClick={() => setRange("24h")}
          disabled={busy}
        >
          24h
        </button>
        <button
          className={range === "7d" ? "primary-action" : "secondary-action"}
          onClick={() => setRange("7d")}
          disabled={busy}
        >
          7d
        </button>
        <a className="secondary-action" href="/" title="Back to live map">
          <Navigation size={16} /> Map
        </a>
      </section>

      {error && <div className="walk-error">{error}</div>}
      {availability && (
        <section className="diagnostics-chart-wrapper">
          <AvailabilityChart data={availability} />
        </section>
      )}
    </main>
  );
}

const path = window.location.pathname;
const isWalkTestRoute = path.startsWith("/walk-test");
const isDiagnosticsRoute = path.startsWith("/diagnostics");

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {isWalkTestRoute ? <WalkTestApp /> : isDiagnosticsRoute ? <DiagnosticsApp /> : <App />}
  </React.StrictMode>
);
